// src/pages/PlantEquipment.tsx — Equipment register, compliance, allocation, defects
// ENHANCED: Add Equipment modal, Work Order system, Service Provider register
import { useState, useMemo, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_EQUIPMENT, getComplianceAlerts, MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { fetchPlantAssets, createPlantAsset, reportDefectToNeon, isNeonAsset } from '../services/plantService'
import {FileText, Paperclip, 
  Truck, Plus, Search, ArrowLeft, X, ChevronRight,
  AlertTriangle, CheckCircle, Calendar, Wrench, ClipboardList,
  Shield, Activity, ChevronDown, Phone, Mail, Star, Send, Building2, Download,
  UserCog, UserX, History, FileDown} from 'lucide-react'
import {
  getCurrentAssignments, getAssignmentHistory, getHistoryForAsset,
  getHistoryForWeek, getWeekRange, assignAssetToEmployee, unassignAsset,
  type CurrentAssignment, type AssignmentHistoryEntry, type AssignableCategory,
} from '../utils/vehicleAssignments'
import {
  downloadWeeklyAssignmentCsv, downloadWeeklyAssignmentJson,
  downloadFullAssignmentHistoryCsv, downloadCurrentAssignmentsCsv,
} from '../utils/vehicleAssignmentReports'

interface EquipDefect {
  id: string
  severity: 'MINOR' | 'MAJOR' | 'CRITICAL'
  description: string
  status: 'OPEN' | 'CLOSED'
  reportedAt: string
}

interface Equip {
  id: string
  assetNumber: string
  category: string
  name: string
  make: string
  model: string
  year: number
  registrationNumber: string | null
  serialNumber: string | null
  status: 'AVAILABLE' | 'ALLOCATED' | 'IN_SERVICE' | 'OUT_OF_SERVICE' | 'DISPOSED'
  currentJobId: string | null
  currentJobNumber: string | null
  ownedOrLeased: string
  purchaseCost: number
  currentValue: number
  regoExpiry: string | null
  ctpExpiry: string | null
  insuranceExpiry: string | null
  nextServiceDate: string | null
  nextServiceKm: number | null
  currentOdometer: number | null
  calibrationDue: string | null
  defects: EquipDefect[]
}

// ─────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────


// ──────────────────────────────────────────────────────────────────
// COMPLIANCE DOCUMENT STORE (prototype — filename + metadata only, no binary until backend)
// Tracks paid renewal invoices and certificates per compliance item.
// ───────────────────────────────────────────────────────────────────
type ComplianceDocType = 'REGO_CERT' | 'CTP_RECEIPT' | 'INSURANCE_CERT' | 'INVOICE' | 'OTHER'

interface ComplianceDocument {
  id:              string
  assetId:         string
  assetNumber:     string
  docType:         ComplianceDocType
  docTypeLabel:    string
  fileName:        string
  fileSize:        string
  referenceNumber: string   // e.g. certificate number, invoice number
  policyNumber:    string   // rego plate / policy number
  providerName:    string   // insurer name, transport department, etc.
  expiryDate:      string | null
  paidDate:        string | null
  uploadedAt:      string
  uploadedByName:  string
  notes:           string
}

const _complianceDocs: ComplianceDocument[] = []

function addComplianceDoc(doc: ComplianceDocument): void {
  _complianceDocs.push(doc)
}
function getComplianceDocs(assetId?: string): ComplianceDocument[] {
  return assetId ? _complianceDocs.filter(d => d.assetId === assetId) : [..._complianceDocs]
}

// ── Compliance status helpers ───────────────────────────────────────────────────
type ComplianceStatus = 'OVERDUE' | 'CRITICAL' | 'WARNING' | 'UPCOMING' | 'CURRENT' | 'NONE'

const COMPLIANCE_META: Record<ComplianceStatus, { label: string; bg: string; text: string; dot: string }> = {
  OVERDUE:  { label: 'OVERDUE',  bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-600'    },
  CRITICAL: { label: '≤14 days',   bg: 'bg-red-50',     text: 'text-red-600',    dot: 'bg-red-400'    },
  WARNING:  { label: '≤30 days',   bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  UPCOMING: { label: '≤60 days',   bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  CURRENT:  { label: 'Current',  bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  NONE:     { label: '—',         bg: 'bg-gray-100',   text: 'text-gray-400',   dot: 'bg-gray-300'   },
}

function complianceStatus(dateStr: string | null): ComplianceStatus {
  if (!dateStr) return 'NONE'
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000)
  if (days < 0)   return 'OVERDUE'
  if (days <= 14) return 'CRITICAL'
  if (days <= 30) return 'WARNING'
  if (days <= 60) return 'UPCOMING'
  return 'CURRENT'
}

function worstComplianceStatus(eq: Equip): ComplianceStatus {
  const order: ComplianceStatus[] = ['OVERDUE','CRITICAL','WARNING','UPCOMING','CURRENT','NONE']
  const statuses = [
    complianceStatus(eq.regoExpiry),
    complianceStatus(eq.ctpExpiry),
    complianceStatus(eq.insuranceExpiry),
  ]
  return statuses.sort((a, b) => order.indexOf(a) - order.indexOf(b))[0]
}

function daysLabel(dateStr: string | null): string {
  if (!dateStr) return '—'
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000)
  if (days < 0)  return `${Math.abs(days)}d overdue`
  if (days === 0) return 'Due today'
  if (days <= 60) return `${days}d`
  return formatDate(dateStr)
}

const CAT_LABELS: Record<string, { label: string; icon: string; prefix: string }> = {
  VEHICLE:         { label: 'Vehicle',          icon: '🚛', prefix: 'VEH' },
  PLANT:           { label: 'Plant',            icon: '🏗',  prefix: 'PLT' },
  POWER_TOOLS:     { label: 'Power tools',      icon: '🔧', prefix: 'PT'  },
  HAND_TOOLS:      { label: 'Hand tools',       icon: '🪛', prefix: 'HT'  },
  TEST_EQUIPMENT:  { label: 'Test equipment',   icon: '🔬', prefix: 'TEST'},
  SAFETY_EQUIPMENT:{ label: 'Safety equipment', icon: '🦺', prefix: 'SAF' },
  COMMUNICATIONS:  { label: 'Communications',   icon: '📻', prefix: 'COM' },
  TRAFFIC_CONTROL: { label: 'Traffic control',  icon: '🚦', prefix: 'TC'  },
  OTHER:           { label: 'Other',            icon: '📦', prefix: 'OTH' },
}

const STATUS_STYLE: Record<string, { dot: string; badge: string; label: string; hex: string }> = {
  AVAILABLE:      { dot: 'bg-green-500',   badge: 'bg-green-100 text-green-700',   label: 'Available',       hex: '#22c55e' },
  ALLOCATED:      { dot: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-700',     label: 'Allocated',       hex: '#3b82f6' },
  IN_SERVICE:     { dot: 'bg-orange-400',  badge: 'bg-orange-100 text-orange-700', label: 'In service',      hex: '#f97316' },
  OUT_OF_SERVICE: { dot: 'bg-red-500',     badge: 'bg-red-100 text-red-700',       label: 'Out of service',  hex: '#ef4444' },
  DISPOSED:       { dot: 'bg-gray-400',    badge: 'bg-gray-100 text-gray-500',     label: 'Disposed',        hex: '#9ca3af' },
}

// ─────────────────────────────────────────────────────────────────
// WORK ORDER TYPES & MOCK DATA
// ─────────────────────────────────────────────────────────────────

type WorkOrderType =
  | 'MECHANICAL_SERVICE' | 'TYRE_REPLACEMENT' | 'ELECTRICAL_AUTOMOTIVE'
  | 'VEHICLE_UPGRADE' | 'BODY_AND_PAINT' | 'REGISTRATION_INSPECTION'
  | 'PLANT_MAINTENANCE' | 'CALIBRATION' | 'FABRICATION' | 'OTHER'

type WorkOrderStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
type WorkOrderPriority = 'ROUTINE' | 'URGENT' | 'CRITICAL'

const WO_TYPE_META: Record<WorkOrderType, { label: string; icon: string }> = {
  MECHANICAL_SERVICE:      { label: 'Mechanical service',       icon: '🔧' },
  TYRE_REPLACEMENT:        { label: 'Tyre replacement',         icon: '🛞' },
  ELECTRICAL_AUTOMOTIVE:   { label: 'Auto-electrical',          icon: '⚡' },
  VEHICLE_UPGRADE:         { label: 'Vehicle upgrade',          icon: '🔩' },
  BODY_AND_PAINT:          { label: 'Body & paint',             icon: '🎨' },
  REGISTRATION_INSPECTION: { label: 'Rego / inspection',        icon: '📋' },
  PLANT_MAINTENANCE:       { label: 'Plant maintenance',        icon: '🏗'  },
  CALIBRATION:             { label: 'Calibration',              icon: '🔬' },
  FABRICATION:             { label: 'Fabrication',              icon: '⚙'  },
  OTHER:                   { label: 'Other service',            icon: '📦' },
}

const WO_STATUS_META: Record<WorkOrderStatus, { label: string; bg: string; text: string }> = {
  DRAFT:       { label: 'Draft',        bg: 'bg-slate-100',  text: 'text-slate-600'  },
  SENT:        { label: 'Sent',         bg: 'bg-blue-100',   text: 'text-blue-700'   },
  ACCEPTED:    { label: 'Accepted',     bg: 'bg-purple-100', text: 'text-purple-700' },
  IN_PROGRESS: { label: 'In progress',  bg: 'bg-amber-100',  text: 'text-amber-700'  },
  COMPLETED:   { label: 'Completed',    bg: 'bg-green-100',  text: 'text-green-700'  },
  CANCELLED:   { label: 'Cancelled',    bg: 'bg-gray-100',   text: 'text-gray-500'   },
}

const PRIORITY_META: Record<WorkOrderPriority, { label: string; color: string }> = {
  ROUTINE:  { label: 'Routine',  color: 'text-slate-600'  },
  URGENT:   { label: 'Urgent',   color: 'text-amber-600'  },
  CRITICAL: { label: 'Critical', color: 'text-red-600'    },
}

interface WorkOrder {
  id: string
  workOrderNumber: string
  equipmentId: string
  equipmentName: string
  assetNumber: string
  workType: WorkOrderType
  title: string
  scope: string
  serviceProviderId: string
  serviceProviderName: string
  priority: WorkOrderPriority
  requiredByDate?: string
  estimatedCost?: number
  actualCost?: number
  chargeToJobId?: string
  status: WorkOrderStatus
  sentAt?: string
  completedAt?: string
  completionNotes?: string
  cancelledReason?: string
  createdById: string
  createdByName: string
  createdAt: string
}

const INITIAL_WORK_ORDERS: WorkOrder[] = [
  {
    id: 'wo1', workOrderNumber: 'WO-2026-001',
    equipmentId: 'eq3', equipmentName: 'Isuzu NPS 75-155 2021', assetNumber: 'VEH-003',
    workType: 'MECHANICAL_SERVICE', title: 'Major service + oil change — VEH-003',
    scope: '15,000km major service. Engine oil & filter, air filter, fuel filter, transmission fluid, brake fluid flush. Check brakes, belts, hoses. Report on rear tyre pressure sensor issue.',
    serviceProviderId: 'sp1', serviceProviderName: 'Brisbane Fleet Mechanical',
    priority: 'URGENT', requiredByDate: '2026-07-10',
    estimatedCost: 680, status: 'SENT',
    sentAt: '2026-06-20T09:00:00Z',
    createdById: 'e8', createdByName: 'Sarah Kim', createdAt: '2026-06-20T08:30:00Z',
  },
  {
    id: 'wo2', workOrderNumber: 'WO-2026-002',
    equipmentId: 'eq2', equipmentName: 'Toyota Hilux SR 2022', assetNumber: 'VEH-002',
    workType: 'TYRE_REPLACEMENT', title: 'Replace all 4 tyres — VEH-002',
    scope: 'Supply and fit 4x Toyo Open Country AT3 tyres 265/65R17. Include wheel balance and wheel alignment. Dispose of old tyres.',
    serviceProviderId: 'sp2', serviceProviderName: 'JAX Tyres Springwood',
    priority: 'ROUTINE', requiredByDate: '2026-07-20',
    estimatedCost: 1240, status: 'DRAFT',
    createdById: 'e8', createdByName: 'Sarah Kim', createdAt: '2026-06-23T14:00:00Z',
  },
  {
    id: 'wo3', workOrderNumber: 'WO-2026-003',
    equipmentId: 'eq1', equipmentName: 'Ford Ranger XL 2023', assetNumber: 'VEH-001',
    workType: 'VEHICLE_UPGRADE', title: 'Install rear toolbox and LED work lights — VEH-001',
    scope: 'Supply and install: (1) Tradesman aluminium underbody toolbox 1500mm to suit Ranger tub. (2) 2x 10W LED reversing work lights rear mounted. (3) Wiring harness with in-cab switch. Powder coat toolbox to match vehicle.',
    serviceProviderId: 'sp3', serviceProviderName: 'Austech Fabrication',
    priority: 'ROUTINE', requiredByDate: '2026-08-01',
    estimatedCost: 2200, status: 'ACCEPTED',
    sentAt: '2026-06-18T10:00:00Z',
    createdById: 'u2', createdByName: 'Jane Smith', createdAt: '2026-06-17T16:00:00Z',
  },
]

// ─────────────────────────────────────────────────────────────────
// SERVICE PROVIDER DATA
// ─────────────────────────────────────────────────────────────────

type ServiceSubCategory =
  | 'MECHANICAL' | 'TYRE_FITTING' | 'ELECTRICAL_AUTOMOTIVE' | 'FABRICATION'
  | 'BODY_SHOP' | 'REGISTRATION_INSPECTION' | 'CALIBRATION_TESTING'
  | 'PLANT_SERVICE' | 'HVAC_REFRIGERATION' | 'OTHER_SERVICE'

const SP_CAT_META: Record<ServiceSubCategory, { label: string; icon: string; bg: string; text: string }> = {
  MECHANICAL:              { label: 'Mechanical',            icon: '🔧', bg: 'bg-slate-100', text: 'text-slate-700'  },
  TYRE_FITTING:            { label: 'Tyres & wheels',        icon: '🛞', bg: 'bg-blue-50',   text: 'text-blue-700'  },
  ELECTRICAL_AUTOMOTIVE:   { label: 'Auto-electrical',       icon: '⚡', bg: 'bg-yellow-50', text: 'text-yellow-700'},
  FABRICATION:             { label: 'Metal fabrication',     icon: '⚙',  bg: 'bg-zinc-100',  text: 'text-zinc-700'  },
  BODY_SHOP:               { label: 'Body & paint',          icon: '🎨', bg: 'bg-pink-50',   text: 'text-pink-700'  },
  REGISTRATION_INSPECTION: { label: 'Rego & inspection',     icon: '📋', bg: 'bg-teal-50',   text: 'text-teal-700'  },
  CALIBRATION_TESTING:     { label: 'Calibration & testing', icon: '🔬', bg: 'bg-purple-50', text: 'text-purple-700'},
  PLANT_SERVICE:           { label: 'Plant & equipment',     icon: '🏗',  bg: 'bg-orange-50', text: 'text-orange-700'},
  HVAC_REFRIGERATION:      { label: 'HVAC / air-con',        icon: '❄',  bg: 'bg-cyan-50',   text: 'text-cyan-700'  },
  OTHER_SERVICE:           { label: 'Other service',         icon: '📦', bg: 'bg-gray-100',  text: 'text-gray-600'  },
}

interface ServiceProvider {
  id: string
  companyName: string
  abn: string
  subCategory: ServiceSubCategory
  contactName: string
  contactPhone: string
  contactEmail: string
  address: string
  servicesOffered: string
  leadTimeDays: number
  isPreferred: boolean
  rating: 1 | 2 | 3 | 4 | 5
  notes?: string
  addedByName: string
  addedAt: string
}

const INITIAL_SERVICE_PROVIDERS: ServiceProvider[] = [
  {
    id: 'sp1', companyName: 'Brisbane Fleet Mechanical',
    abn: '42 123 456 001', subCategory: 'MECHANICAL',
    contactName: 'Dean Harris', contactPhone: '07 3278 5500', contactEmail: 'service@brisfleet.com.au',
    address: '14 Industrial Ave, Acacia Ridge QLD 4110',
    servicesOffered: 'General mechanical, major/minor service, brake & clutch, air-con regas, fleet maintenance contracts',
    leadTimeDays: 2, isPreferred: true, rating: 5,
    notes: 'Preferred mechanical provider. Fleet account set up — 30-day payment terms. Ask for Dean.',
    addedByName: 'Jane Smith', addedAt: '2026-02-10T09:00:00Z',
  },
  {
    id: 'sp2', companyName: 'JAX Tyres Springwood',
    abn: '55 234 567 002', subCategory: 'TYRE_FITTING',
    contactName: 'Alicia Wong', contactPhone: '07 3808 1100', contactEmail: 'springwood@jaxtyres.com.au',
    address: '189 Compton Rd, Springwood QLD 4127',
    servicesOffered: 'Tyre supply & fitting (all vehicle types), wheel balancing, wheel alignment, puncture repair, fleet accounts',
    leadTimeDays: 1, isPreferred: true, rating: 4,
    addedByName: 'Jane Smith', addedAt: '2026-02-10T09:05:00Z',
  },
  {
    id: 'sp3', companyName: 'Austech Fabrication',
    abn: '77 345 678 003', subCategory: 'FABRICATION',
    contactName: 'Brett Schultz', contactPhone: '07 3372 8820', contactEmail: 'brett@austech.com.au',
    address: '8 Forge St, Richlands QLD 4077',
    servicesOffered: 'Custom toolboxes, tray builds, bullbars, tow bars, underbody storage, powder coating, fleet signwriting mounting',
    leadTimeDays: 7, isPreferred: true, rating: 5,
    notes: 'Lead time varies with fabrication complexity. Always get written quote for work >$500.',
    addedByName: 'Jane Smith', addedAt: '2026-03-01T09:00:00Z',
  },
  {
    id: 'sp4', companyName: 'Powerline Auto-Electrical',
    abn: '63 456 789 004', subCategory: 'ELECTRICAL_AUTOMOTIVE',
    contactName: 'Kevin Tran', contactPhone: '07 3200 4456', contactEmail: 'kevin@powerlineauto.com.au',
    address: '22 Boundary Rd, Coopers Plains QLD 4108',
    servicesOffered: 'LED lighting upgrades (headlights, worklights, lightbars), trailer wiring, alternators, dual battery systems, UHF/CB radio installation',
    leadTimeDays: 3, isPreferred: false, rating: 4,
    addedByName: 'Sarah Kim', addedAt: '2026-04-15T09:00:00Z',
  },
  {
    id: 'sp5', companyName: 'QLD Mobile Plant Services',
    abn: '88 567 890 005', subCategory: 'PLANT_SERVICE',
    contactName: 'Craig McDonald', contactPhone: '0418 775 320', contactEmail: 'craig@qldplant.com.au',
    address: 'Mobile — services Brisbane South and Logan',
    servicesOffered: 'Excavator service and repair (Kubota, Bobcat, Yanmar), tipper truck service, hydraulic hose replacement, tracks and undercarriage, mobile on-site service',
    leadTimeDays: 5, isPreferred: true, rating: 4,
    notes: 'Mobile service — comes to site or depot. Preferred for Kubota KX016 service.',
    addedByName: 'Jane Smith', addedAt: '2026-02-20T09:00:00Z',
  },
]

// ─────────────────────────────────────────────────────────────────
// HELPER: next asset number
// ─────────────────────────────────────────────────────────────────

function nextAssetNumber(category: string, existing: { assetNumber: string }[]): string {
  const prefix = CAT_LABELS[category]?.prefix ?? 'OTH'
  const nums = existing
    .map(e => e.assetNumber)
    .filter(n => n.startsWith(prefix + '-'))
    .map(n => parseInt(n.split('-').pop() ?? '0', 10))
    .filter(n => !isNaN(n))
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1
  return `${prefix}-${String(next).padStart(3, '0')}`
}

// ─────────────────────────────────────────────────────────────────
// ADD EQUIPMENT MODAL
// ─────────────────────────────────────────────────────────────────

function AddEquipmentModal({ onClose, onAdd, existing }: {
  onClose: () => void
  onAdd:   (e: Equip) => void
  existing: Equip[]
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    category: 'VEHICLE', name: '', make: '', model: '', year: '', serialNumber: '',
    registrationNumber: '', ownedOrLeased: 'OWNED', purchaseCost: '', currentValue: '',
    regoExpiry: '', ctpExpiry: '', insuranceExpiry: '', nextServiceDate: '',
    nextServiceKm: '', currentOdometer: '', calibrationDue: '', status: 'AVAILABLE', notes: '',
  })
  const [assetNumber, setAssetNumber] = useState(() => nextAssetNumber('VEHICLE', existing))
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleCatChange = (cat: string) => {
    set('category', cat)
    setAssetNumber(nextAssetNumber(cat, existing))
  }

  const isVehicle = ['VEHICLE'].includes(form.category)
  const isTestEquip = form.category === 'TEST_EQUIPMENT'
  const canSave = form.name.trim() && assetNumber.trim()

  const handleSave = () => {
    if (!canSave) return
    const newEquip: Equip = {
      id:                 `eq_${Date.now()}`,
      assetNumber:        assetNumber.trim(),
      category:           form.category,
      name:               form.name.trim(),
      make:               form.make.trim() || null,
      model:              form.model.trim() || null,
      year:               form.year ? parseInt(form.year) : null,
      registrationNumber: isVehicle ? (form.registrationNumber.trim() || null) : null,
      serialNumber:       form.serialNumber.trim() || null,
      status:             form.status as any,
      currentJobId:       null,
      currentJobNumber:   null,
      ownedOrLeased:      form.ownedOrLeased,
      purchaseCost:       form.purchaseCost ? parseFloat(form.purchaseCost) : null,
      currentValue:       form.currentValue ? parseFloat(form.currentValue) : null,
      regoExpiry:         form.regoExpiry || null,
      ctpExpiry:          form.ctpExpiry || null,
      insuranceExpiry:    form.insuranceExpiry || null,
      nextServiceDate:    form.nextServiceDate || null,
      nextServiceKm:      form.nextServiceKm ? parseInt(form.nextServiceKm) : null,
      currentOdometer:    form.currentOdometer ? parseInt(form.currentOdometer) : null,
      calibrationDue:     isTestEquip ? (form.calibrationDue || null) : null,
      defects:            [],
    } as any
    onAdd(newEquip)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Register new equipment</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Category + Asset # */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Category *</label>
              <select value={form.category} onChange={e => handleCatChange(e.target.value)} className="input w-full text-sm">
                {Object.entries(CAT_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Asset number *</label>
              <input value={assetNumber} onChange={e => setAssetNumber(e.target.value)}
                className="input w-full text-sm font-mono" placeholder="e.g. VEH-005" />
            </div>
          </div>

          {/* Name / description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name / description *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Ford Ranger XL 2023, Kubota Excavator KX016, Hilti Drill" className="input w-full text-sm" />
          </div>

          {/* Make / Model / Year */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Make</label>
              <input value={form.make} onChange={e => set('make', e.target.value)} placeholder="e.g. Ford" className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Model</label>
              <input value={form.model} onChange={e => set('model', e.target.value)} placeholder="e.g. Ranger XL" className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Year</label>
              <input value={form.year} onChange={e => set('year', e.target.value)} placeholder="2024" type="number" className="input w-full text-sm" />
            </div>
          </div>

          {/* Serial / Rego */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Serial / VIN number</label>
              <input value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} placeholder="VIN or serial number" className="input w-full text-sm" />
            </div>
            {isVehicle && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Registration number</label>
                <input value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} placeholder="e.g. 1ABC 234" className="input w-full text-sm" />
              </div>
            )}
          </div>

          {/* Owned / leased + status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Owned or leased *</label>
              <select value={form.ownedOrLeased} onChange={e => set('ownedOrLeased', e.target.value)} className="input w-full text-sm">
                <option value="OWNED">Owned</option>
                <option value="LEASED">Leased</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Initial status *</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className="input w-full text-sm">
                <option value="AVAILABLE">Available</option>
                <option value="IN_SERVICE">In service</option>
                <option value="OUT_OF_SERVICE">Out of service</option>
              </select>
            </div>
          </div>

          {/* Costs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Purchase cost</label>
              <input value={form.purchaseCost} onChange={e => set('purchaseCost', e.target.value)} placeholder="0.00" type="number" className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Current value</label>
              <input value={form.currentValue} onChange={e => set('currentValue', e.target.value)} placeholder="0.00" type="number" className="input w-full text-sm" />
            </div>
          </div>

          {/* Compliance dates */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Compliance dates</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {isVehicle && <>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Registration expiry</label>
                  <input type="date" value={form.regoExpiry} onChange={e => set('regoExpiry', e.target.value)} className="input w-full text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">CTP expiry</label>
                  <input type="date" value={form.ctpExpiry} onChange={e => set('ctpExpiry', e.target.value)} className="input w-full text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Insurance expiry</label>
                  <input type="date" value={form.insuranceExpiry} onChange={e => set('insuranceExpiry', e.target.value)} className="input w-full text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Next service date</label>
                  <input type="date" value={form.nextServiceDate} onChange={e => set('nextServiceDate', e.target.value)} className="input w-full text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Service at (km)</label>
                  <input type="number" value={form.nextServiceKm} onChange={e => set('nextServiceKm', e.target.value)} placeholder="75000" className="input w-full text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Current odometer</label>
                  <input type="number" value={form.currentOdometer} onChange={e => set('currentOdometer', e.target.value)} placeholder="0" className="input w-full text-sm" />
                </div>
              </>}
              {!isVehicle && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Next service date</label>
                  <input type="date" value={form.nextServiceDate} onChange={e => set('nextServiceDate', e.target.value)} className="input w-full text-sm" />
                </div>
              )}
              {isTestEquip && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Calibration due</label>
                  <input type="date" value={form.calibrationDue} onChange={e => set('calibrationDue', e.target.value)} className="input w-full text-sm" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors">
            <Plus className="w-4 h-4" /> Register equipment
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// CREATE WORK ORDER MODAL
// ─────────────────────────────────────────────────────────────────

function CreateWorkOrderModal({ preEquip, equipment, serviceProviders, onClose, onSave }: {
  preEquip?: Equip
  equipment: Equip[]
  serviceProviders: ServiceProvider[]
  onClose: () => void
  onSave:  (wo: WorkOrder) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    equipmentId:      preEquip?.id ?? '',
    workType:         'MECHANICAL_SERVICE' as WorkOrderType,
    title:            '',
    scope:            '',
    serviceProviderId:'',
    priority:         'ROUTINE' as WorkOrderPriority,
    requiredByDate:   '',
    estimatedCost:    '',
    chargeToJobId:    '',
    sendNow:          true,
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const selEquip = equipment.find(e => e.id === form.equipmentId) ?? preEquip
  const selSP    = serviceProviders.find(s => s.id === form.serviceProviderId)
  const canSave  = form.equipmentId && form.title.trim() && form.serviceProviderId

  const handleSave = () => {
    if (!canSave) return
    const sp = serviceProviders.find(s => s.id === form.serviceProviderId)!
    const eq = equipment.find(e => e.id === form.equipmentId) ?? preEquip!
    const now = new Date().toISOString()
    const wo: WorkOrder = {
      id: `wo_${Date.now()}`,
      workOrderNumber: `WO-2026-${String(INITIAL_WORK_ORDERS.length + 1).padStart(3, '0')}`,
      equipmentId: form.equipmentId,
      equipmentName: eq.name,
      assetNumber: eq.assetNumber,
      workType: form.workType,
      title: form.title.trim(),
      scope: form.scope.trim(),
      serviceProviderId: form.serviceProviderId,
      serviceProviderName: sp.companyName,
      priority: form.priority,
      requiredByDate: form.requiredByDate || undefined,
      estimatedCost: form.estimatedCost ? parseFloat(form.estimatedCost) : undefined,
      chargeToJobId: form.chargeToJobId || undefined,
      status: form.sendNow ? 'SENT' : 'DRAFT',
      sentAt: form.sendNow ? now : undefined,
      createdById: user?.id ?? 'u1',
      createdByName: user?.fullName ?? 'Current User',
      createdAt: now,
    }
    onSave(wo)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Create work order</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Equipment */}
          {!preEquip && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Equipment *</label>
              <select value={form.equipmentId} onChange={e => set('equipmentId', e.target.value)} className="input w-full text-sm">
                <option value="">— Select equipment —</option>
                {equipment.map(e => (
                  <option key={e.id} value={e.id}>
                    {CAT_LABELS[e.category]?.icon} {e.assetNumber} — {e.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {preEquip && (
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
              <span className="text-xl">{CAT_LABELS[preEquip.category]?.icon ?? '📦'}</span>
              <div>
                <p className="text-sm font-semibold text-slate-800">{preEquip.name}</p>
                <p className="text-xs text-slate-400">{preEquip.assetNumber}</p>
              </div>
            </div>
          )}

          {/* Work type + priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Work type *</label>
              <select value={form.workType} onChange={e => set('workType', e.target.value)} className="input w-full text-sm">
                {(Object.keys(WO_TYPE_META) as WorkOrderType[]).map(t => (
                  <option key={t} value={t}>{WO_TYPE_META[t].icon} {WO_TYPE_META[t].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Priority *</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value as WorkOrderPriority)} className="input w-full text-sm">
                <option value="ROUTINE">Routine</option>
                <option value="URGENT">Urgent</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Replace all four tyres — VEH-002"
              className="input w-full text-sm" />
          </div>

          {/* Scope */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Scope of work</label>
            <textarea value={form.scope} onChange={e => set('scope', e.target.value)}
              placeholder="Detailed description of work required, specifications, part numbers..."
              className="input w-full resize-none h-24 text-sm" />
          </div>

          {/* Service provider */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Service provider *</label>
            <select value={form.serviceProviderId} onChange={e => set('serviceProviderId', e.target.value)} className="input w-full text-sm">
              <option value="">— Select service provider —</option>
              {serviceProviders.map(sp => (
                <option key={sp.id} value={sp.id}>
                  {SP_CAT_META[sp.subCategory]?.icon} {sp.companyName}{sp.isPreferred ? ' ★' : ''}
                </option>
              ))}
            </select>
            {selSP && (
              <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-3">
                <Phone className="w-3 h-3 flex-shrink-0" /> {selSP.contactPhone}
                <span>·</span>
                <span className="text-slate-400">Lead {selSP.leadTimeDays}d</span>
              </div>
            )}
          </div>

          {/* Dates + cost */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Required by</label>
              <input type="date" value={form.requiredByDate} onChange={e => set('requiredByDate', e.target.value)} className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Estimated cost (ex GST)</label>
              <input type="number" value={form.estimatedCost} onChange={e => set('estimatedCost', e.target.value)}
                placeholder="0.00" className="input w-full text-sm" />
            </div>
          </div>

          {/* Charge to job */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Charge to job (optional)</label>
            <select value={form.chargeToJobId} onChange={e => set('chargeToJobId', e.target.value)} className="input w-full text-sm">
              <option value="">— Not job-costed —</option>
              {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
            </select>
          </div>

          {/* Send now toggle */}
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 border border-slate-200">
            <div onClick={() => set('sendNow', !form.sendNow)}
              className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${form.sendNow ? 'bg-indigo-600' : 'bg-slate-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.sendNow ? 'translate-x-4' : ''}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Send to service provider now</p>
              <p className="text-xs text-slate-400">Uncheck to save as draft for review before sending</p>
            </div>
          </label>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors">
            <Send className="w-4 h-4" /> {form.sendNow ? 'Create & send' : 'Save draft'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ADD SERVICE PROVIDER MODAL
// ─────────────────────────────────────────────────────────────────

function AddServiceProviderModal({ onClose, onAdd }: {
  onClose: () => void
  onAdd:   (sp: ServiceProvider) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    companyName: '', abn: '', subCategory: 'MECHANICAL' as ServiceSubCategory,
    contactName: '', contactPhone: '', contactEmail: '',
    address: '', servicesOffered: '', leadTimeDays: '3', isPreferred: false, notes: '',
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))
  const canSave = form.companyName.trim() && form.contactPhone.trim()

  const handleSave = () => {
    if (!canSave) return
    onAdd({
      id:              `sp_${Date.now()}`,
      companyName:     form.companyName.trim(),
      abn:             form.abn.trim(),
      subCategory:     form.subCategory,
      contactName:     form.contactName.trim(),
      contactPhone:    form.contactPhone.trim(),
      contactEmail:    form.contactEmail.trim(),
      address:         form.address.trim(),
      servicesOffered: form.servicesOffered.trim(),
      leadTimeDays:    parseInt(form.leadTimeDays) || 3,
      isPreferred:     form.isPreferred,
      rating:          3,
      notes:           form.notes.trim() || undefined,
      addedByName:     user?.fullName ?? 'Current User',
      addedAt:         new Date().toISOString(),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Register service provider</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Company name *</label>
            <input value={form.companyName} onChange={e => set('companyName', e.target.value)}
              placeholder="e.g. Brisbane Fleet Mechanical" className="input w-full text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Service type *</label>
              <select value={form.subCategory} onChange={e => set('subCategory', e.target.value)} className="input w-full text-sm">
                {(Object.keys(SP_CAT_META) as ServiceSubCategory[]).map(k => (
                  <option key={k} value={k}>{SP_CAT_META[k].icon} {SP_CAT_META[k].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">ABN</label>
              <input value={form.abn} onChange={e => set('abn', e.target.value)}
                placeholder="XX XXX XXX XXX" className="input w-full text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contact name</label>
              <input value={form.contactName} onChange={e => set('contactName', e.target.value)}
                placeholder="Primary contact" className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contact phone *</label>
              <input value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)}
                placeholder="07 XXXX XXXX" className="input w-full text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Contact email</label>
            <input type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)}
              placeholder="service@provider.com.au" className="input w-full text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Business address</label>
            <input value={form.address} onChange={e => set('address', e.target.value)}
              placeholder="Street, Suburb QLD XXXX" className="input w-full text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Services offered</label>
            <textarea value={form.servicesOffered} onChange={e => set('servicesOffered', e.target.value)}
              placeholder="Describe specific services — e.g. vehicle servicing, tyre supply & fit, LED lighting upgrades..."
              className="input w-full resize-none h-20 text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Typical lead time (days)</label>
              <input type="number" value={form.leadTimeDays} onChange={e => set('leadTimeDays', e.target.value)}
                className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Trading hours, account terms..." className="input w-full text-sm" />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.isPreferred} onChange={e => set('isPreferred', e.target.checked)}
              className="w-4 h-4 accent-amber-500" />
            <span className="text-sm text-slate-700">Mark as preferred / vetted service provider</span>
          </label>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors">
            <Building2 className="w-4 h-4" /> Register provider
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ALLOCATE / DEFECT MODALS (unchanged)
// ─────────────────────────────────────────────────────────────────

function AllocateModal({ equip, onClose, onAllocate }: { equip: Equip; onClose: () => void; onAllocate: (jobId: string, jobNumber: string) => void }) {
  const [jobId, setJobId] = useState('')
  const job = MOCK_JOBS.find(j => j.id === jobId)
  // HP7 / TS12: Block allocation if asset has any OPEN defects
  const openDefects = (equip.defects ?? []).filter((d: any) => d.status === 'OPEN')
  const hasOpenDefect = openDefects.length > 0
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Allocate {equip.assetNumber}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {/* HP7: Defect block warning */}
          {hasOpenDefect && (
            <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-800">Allocation blocked — open defect{openDefects.length !== 1 ? 's' : ''}</p>
                <p className="text-xs text-red-700 mt-0.5">{equip.assetNumber} has {openDefects.length} open defect{openDefects.length !== 1 ? 's' : ''}. Close all defects before allocating this asset to a job.</p>
                {openDefects.slice(0, 2).map((d: any, i: number) => (
                  <p key={i} className="text-xs text-red-600 mt-1">&#x2022; {d.severity}: {d.description?.slice(0, 60)}</p>
                ))}
              </div>
            </div>
          )}
          <select className="input w-full text-sm" value={jobId} onChange={e => setJobId(e.target.value)} disabled={hasOpenDefect}>
            <option value="">Select job…</option>
            {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j =>
              <option key={j.id} value={j.id}>{j.jobNumber} — {j.description?.slice(0,30)}</option>
            )}
          </select>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose} className="flex-1 btn-secondary text-sm">Cancel</button>
          <button onClick={() => { if (job) { onAllocate(job.id, job.jobNumber); onClose() } }}
            disabled={!jobId || hasOpenDefect} className={`flex-1 btn-primary text-sm disabled:opacity-40 ${hasOpenDefect ? 'cursor-not-allowed' : ''}`}>
            {hasOpenDefect ? 'Defect — cannot allocate' : 'Allocate'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DefectModal({ equip, onClose, onSubmit }: { equip: Equip; onClose: () => void; onSubmit: (desc: string, sev: EquipDefect['severity']) => void }) {
  const [description, setDescription] = useState('')
  const [severity, setSeverity]       = useState<EquipDefect['severity']>('MINOR')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Report defect — {equip.assetNumber}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-2">
            {(['MINOR', 'MAJOR', 'CRITICAL'] as EquipDefect['severity'][]).map(s => (
              <button key={s} type="button" onClick={() => setSeverity(s)}
                className={`flex-1 text-xs font-medium py-1.5 rounded-lg border transition-colors ${severity===s?s==='CRITICAL'?'bg-red-600 text-white border-red-600':s==='MAJOR'?'bg-amber-500 text-white border-amber-500':'bg-blue-600 text-white border-blue-600':'border-gray-200 text-gray-500 hover:border-gray-300'}`}>{s}</button>
            ))}
          </div>
          <textarea className="input w-full resize-none h-24 text-sm" placeholder="Describe the defect…" value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose} className="flex-1 btn-secondary text-sm">Cancel</button>
          <button onClick={() => { if (description) { onSubmit(description, severity); onClose() } }}
            disabled={!description} className={`flex-1 btn-primary text-sm disabled:opacity-40 ${severity==='CRITICAL'?'bg-red-600 hover:bg-red-700':''}`}>Submit defect</button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// EQUIPMENT DETAIL (with Work Orders tab added)
// ─────────────────────────────────────────────────────────────────

function EquipDetail({ equip, workOrders, serviceProviders, equipment, onBack, onAllocate, onDeallocate, onDefect, onCreateWO, onOpenAssign, currentAssignment, onCompleteWO }: {
  equip:            Equip
  workOrders:       WorkOrder[]
  serviceProviders: ServiceProvider[]
  equipment:        Equip[]
  onBack:           () => void
  onAllocate:       (jobId: string, jobNum: string) => void
  onDeallocate:     () => void
  onDefect:         (desc: string, sev: EquipDefect['severity']) => void
  onCreateWO:       (wo: WorkOrder) => void
  onOpenAssign:     () => void
  currentAssignment: CurrentAssignment | null
  onCompleteWO?:    (woId: string, equipId: string, nextServiceDate: string) => void
}) {
  const { isTier } = useAuthStore()
  const [tab, setTab]           = useState<'details'|'compliance'|'defects'|'workorders'>('details')
  const [showAlloc, setShowAlloc]                 = useState(false)
  const [showDefect, setShowDefect]               = useState(false)
  const [showWO, setShowWO]                       = useState(false)
  const [showUpdateCompliance, setShowUpdateCompliance] = useState(false)

  const ss = STATUS_STYLE[equip.status] ?? STATUS_STYLE.AVAILABLE
  const cat = CAT_LABELS[equip.category] ?? { label: equip.category, icon: '📦', prefix: 'OTH' }
  const canManage = isTier('B_PC')
  const myWOs = workOrders.filter(w => w.equipmentId === equip.id)
  const openWOs = myWOs.filter(w => !['COMPLETED','CANCELLED'].includes(w.status))

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Equipment register
      </button>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-lg font-bold text-gray-900">{equip.assetNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.badge}`}>{ss.label}</span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{cat.icon} {cat.label}</span>
              </div>
              <h1 className="text-base font-medium text-gray-700">{equip.name}</h1>
              {equip.currentJobNumber && <p className="text-sm text-blue-600 mt-0.5">Allocated: {equip.currentJobNumber}</p>}
              {(equip.category === 'VEHICLE' || equip.category === 'PLANT') && (
                <p className="text-sm mt-0.5 flex items-center gap-1">
                  <UserCog className="w-3.5 h-3.5 text-gray-400" />
                  {currentAssignment
                    ? <span className="text-gray-700">Assigned to <strong>{currentAssignment.employeeName}</strong></span>
                    : <span className="text-gray-400">Unassigned (pool)</span>}
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {canManage && (equip.category === 'VEHICLE' || equip.category === 'PLANT') && (
                <button onClick={onOpenAssign} className="btn-secondary text-xs flex items-center gap-1">
                  <UserCog className="w-3.5 h-3.5" />{currentAssignment ? 'Reassign' : 'Assign to employee'}
                </button>
              )}
              {canManage && equip.status === 'AVAILABLE' && <button onClick={() => setShowAlloc(true)} className="btn-primary text-xs">Allocate to job</button>}
              {canManage && equip.status === 'ALLOCATED' && <button onClick={onDeallocate} className="btn-secondary text-xs">Return to yard</button>}
              {canManage && <button onClick={() => setShowWO(true)} className="btn-secondary text-xs flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" />Create work order</button>}
              <button onClick={() => setShowDefect(true)} className="btn-secondary text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Report defect</button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 border-t text-sm">
          {[
            { label: 'Make / model', value: `${equip.make ?? ''} ${equip.model ?? ''}`.trim() || '—' },
            { label: 'Year',         value: equip.year ? String(equip.year) : '—' },
            { label: 'Owned / leased', value: equip.ownedOrLeased ?? '—' },
            { label: 'Purchase cost',  value: equip.purchaseCost ? formatCurrency(equip.purchaseCost) : '—' },
          ].map(f => (
            <div key={f.label} className="px-5 py-3">
              <div className="text-xs text-gray-500">{f.label}</div>
              <div className="font-medium mt-0.5">{f.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 flex-wrap">
        {([
          { id: 'details',    label: 'Details' },
          { id: 'compliance', label: 'Compliance' },
          { id: 'defects',    label: `Defects (${equip.defects.filter((d:any)=>d.status==='OPEN').length})` },
          { id: 'workorders', label: `Work orders (${openWOs.length})`, alert: openWOs.length > 0 },
        ] as const).map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors relative ${tab===t.id?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {(t as any).alert && <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-amber-500 rounded-full" />}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {tab === 'details' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="divide-y divide-gray-100">
            {[
              ['Serial / VIN',       equip.serialNumber    ],
              ['Registration',       equip.registrationNumber],
              ...((equip.category === 'VEHICLE' || equip.category === 'PLANT') ? [
                ['Assigned to', currentAssignment
                  ? currentAssignment.employeeName + ' (since ' + formatDate(currentAssignment.effectiveDate) + ')'
                  : 'Unassigned (pool)'],
              ] : []),
              ['Status',             ss.label              ],
              ['Current job',        equip.currentJobNumber ],
              ['Current odometer',   equip.currentOdometer != null ? `${equip.currentOdometer.toLocaleString()} km` : null],
              ['Next service at',    equip.nextServiceKm   != null ? `${equip.nextServiceKm.toLocaleString()} km`   : null],
            ].filter(([,v])=>v).map(([l,v])=>(
              <div key={String(l)} className="flex items-center gap-4 px-5 py-3 text-sm">
                <span className="text-gray-500 w-36 flex-shrink-0">{l}</span>
                <span className="font-medium text-gray-900">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compliance tab */}
      {tab === 'compliance' && (
        <div className="space-y-3">
          {canManage && (
            <div className="flex justify-end">
              <button onClick={() => setShowUpdateCompliance(true)}
                className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Shield className="w-3 h-3" />
                Update compliance dates
              </button>
            </div>
          )}
          <div className="space-y-2">
          {[
            { label: 'Registration expiry', date: equip.regoExpiry     },
            { label: 'CTP expiry',          date: equip.ctpExpiry      },
            { label: 'Insurance expiry',    date: equip.insuranceExpiry},
            { label: 'Next service date',   date: equip.nextServiceDate},
            { label: 'Calibration due',     date: equip.calibrationDue },
          ].filter(f=>f.date).map(f => {
            const days = f.date ? Math.ceil((new Date(f.date).getTime()-Date.now())/86400000) : 999
            const color = days<0?'text-red-600':days<=30?'text-amber-600':'text-green-600'
            const bg    = days<0?'bg-red-50 border-red-200':days<=30?'bg-amber-50 border-amber-200':'bg-white border-gray-200'
            return (
              <div key={f.label} className={`flex items-center justify-between px-5 py-3 rounded-xl border text-sm ${bg}`}>
                <span className="text-gray-700">{f.label}</span>
                <span className={`font-semibold ${color}`}>{f.date?formatDate(f.date):'—'}{days<0?' (OVERDUE)':days<=30?` (${days}d)`:''}</span>
              </div>
            )
          })}
          </div>
        </div>
      )}

      {/* Defects tab */}
      {tab === 'defects' && (
        <div className="space-y-2">
          {equip.defects.length===0
            ? <div className="text-center py-10 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">No defects recorded</div>
            : equip.defects.map((d:any)=>(
              <div key={d.id} className={`flex items-start gap-3 px-5 py-4 rounded-xl border text-sm ${d.severity==='CRITICAL'?'bg-red-50 border-red-200':d.severity==='MAJOR'?'bg-amber-50 border-amber-200':'bg-white border-gray-200'}`}>
                <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${d.severity==='CRITICAL'?'text-red-600':d.severity==='MAJOR'?'text-amber-600':'text-blue-500'}`}/>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{d.description}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{d.severity} · {d.status} · {d.reportedAt}</p>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* Work Orders tab */}
      {tab === 'workorders' && (
        <div className="space-y-3">
          {canManage && (
            <button onClick={() => setShowWO(true)}
              className="flex items-center gap-2 btn-primary text-sm">
              <Plus className="w-4 h-4" /> Create work order
            </button>
          )}
          {myWOs.length === 0
            ? <div className="text-center py-10 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">No work orders for this equipment</div>
            : myWOs.sort((a,b)=>b.createdAt.localeCompare(a.createdAt)).map(wo => {
              const sm = WO_STATUS_META[wo.status]
              const tm = WO_TYPE_META[wo.workType]
              return (
                <div key={wo.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                  <span className="text-xl flex-shrink-0">{tm.icon}</span>
                  <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-mono text-gray-400">{wo.workOrderNumber}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sm.bg} ${sm.text}`}>{sm.label}</span>
                  <span className={`text-xs font-semibold ${PRIORITY_META[wo.priority].color}`}>{wo.priority}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{wo.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{wo.serviceProviderName}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                  {wo.estimatedCost && <span>{formatCurrency(wo.estimatedCost)} est.</span>}
                  {wo.requiredByDate && <span>Due {formatDate(wo.requiredByDate)}</span>}
                  <span>By {wo.createdByName}</span>
                  </div>
                    {/* P3-8: Mark complete button — updates WO status and nextServiceDate */}
                      {canManage && !['COMPLETED','CANCELLED'].includes(wo.status) && onCompleteWO && (
                      <button
                        onClick={() => {
                          const next = new Date()
                          next.setMonth(next.getMonth() + 3)  // 3-month / 15,000km service interval
                          onCompleteWO(wo.id, equip.id, next.toISOString().slice(0, 10))
                        }}
                        className="mt-2 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 w-fit"
                      >
                        <CheckCircle className="w-3 h-3" />Mark complete
                      </button>
                    )}
                  </div>
                </div>
                </div>
              )
            })
          }
        </div>
      )}

      {showAlloc  && <AllocateModal equip={equip} onClose={() => setShowAlloc(false)}  onAllocate={onAllocate} />}
      {showUpdateCompliance && (
        <UpdateComplianceDatesModal
          equip={equip}
          currentUserName={'PM/PC'}
          onSave={(id, changes) => { /* updateEquip called via onUpdate prop if added */ }}
          onClose={() => setShowUpdateCompliance(false)}
        />
      )}
      {showDefect && <DefectModal   equip={equip} onClose={() => setShowDefect(false)} onSubmit={onDefect} />}
      {showWO && <CreateWorkOrderModal preEquip={equip} equipment={equipment}
        serviceProviders={serviceProviders} onClose={() => setShowWO(false)} onSave={wo => { onCreateWO(wo); setTab('workorders') }} />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 1 — FLEET COMPLIANCE REPORT
// Prints all assets with compliance dates colour-coded by urgency.
// Red = overdue/expired, Amber = due within 30 days, Green = OK.
// Required for insurance renewals and statutory compliance.
// ─────────────────────────────────────────────────────────────────
function generateFleetComplianceHTML(equipment: any[]) {
  const now   = new Date()
  const today = now.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

  const daysDiff = (dateStr: string | null) => {
    if (!dateStr) return null
    return Math.ceil((new Date(dateStr).getTime() - now.getTime()) / 86400000)
  }
  const cellColor = (days: number | null) => {
    if (days === null) return '#f9fafb'
    if (days < 0)   return '#fee2e2'   // Red — overdue
    if (days <= 30) return '#fef3c7'   // Amber — due soon
    return '#f0fdf4'                    // Green — OK
  }
  const cellText = (days: number | null, dateStr: string | null) => {
    if (!dateStr) return '<span style="color:#9ca3af">—</span>'
    const d = new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    if (days! < 0)   return `<span style="color:#b91c1c;font-weight:bold">⚠ OVERDUE<br><small>${d}</small></span>`
    if (days! <= 30) return `<span style="color:#b45309;font-weight:bold">⚠ ${days}d<br><small>${d}</small></span>`
    return `<span style="color:#166534">${d}</span>`
  }

  const rows = equipment.map((eq, i) => {
    const rego   = daysDiff(eq.regoExpiry)
    const ctp    = daysDiff(eq.ctpExpiry)
    const ins    = daysDiff(eq.insuranceExpiry)
    const svc    = daysDiff(eq.nextServiceDate)
    const calib  = daysDiff(eq.calibrationDue)
    const rowBg  = i % 2 === 0 ? '#ffffff' : '#f9fafb'
    const statusColor = eq.status === 'OUT_OF_SERVICE' ? '#ef4444' : eq.status === 'IN_SERVICE' ? '#f97316' : '#22c55e'
    return `<tr style="background:${rowBg}">
      <td style="padding:6px 10px;font-family:monospace;font-weight:bold">${eq.assetNumber}</td>
      <td style="padding:6px 10px">${eq.name}</td>
      <td style="padding:6px 10px"><span style="color:${statusColor};font-weight:bold">${eq.status?.replace(/_/g,' ')}</span></td>
      <td style="padding:6px 10px;text-align:center;background:${cellColor(rego)}">${cellText(rego, eq.regoExpiry)}</td>
      <td style="padding:6px 10px;text-align:center;background:${cellColor(ctp)}">${cellText(ctp, eq.ctpExpiry)}</td>
      <td style="padding:6px 10px;text-align:center;background:${cellColor(ins)}">${cellText(ins, eq.insuranceExpiry)}</td>
      <td style="padding:6px 10px;text-align:center;background:${cellColor(svc)}">${cellText(svc, eq.nextServiceDate)}</td>
      ${calib !== null ? `<td style="padding:6px 10px;text-align:center;background:${cellColor(calib)}">${cellText(calib, eq.calibrationDue)}</td>` : '<td style="padding:6px 10px;text-align:center;color:#9ca3af">n/a</td>'}
    </tr>`
  }).join('')

  const overdue = equipment.filter(e => {
    const dates = [e.regoExpiry, e.ctpExpiry, e.insuranceExpiry, e.nextServiceDate, e.calibrationDue]
    return dates.some(d => d && daysDiff(d)! < 0)
  }).length
  const dueSoon = equipment.filter(e => {
    const dates = [e.regoExpiry, e.ctpExpiry, e.insuranceExpiry, e.nextServiceDate, e.calibrationDue]
    return dates.some(d => d && daysDiff(d)! >= 0 && daysDiff(d)! <= 30)
  }).length

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Fleet Compliance Report — ${today}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:14px}
table{width:100%;border-collapse:collapse;font-size:9.5pt}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:center;font-size:9pt}
th:first-child,th:nth-child(2){text-align:left}
.kpi{display:flex;gap:12px;margin-bottom:14px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px 12px;text-align:center}
.kpi-v{font-size:18pt;font-weight:900}.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:6px}
.legend{display:flex;gap:16px;margin-bottom:10px;font-size:9pt}
.leg{display:flex;align-items:center;gap:6px}
.leg-box{width:14px;height:14px;border-radius:3px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:17pt;font-weight:900;color:#1B3A6B">FLEET COMPLIANCE REPORT</div>
    <div style="font-size:9pt;color:#888">As at: ${today}</div></div>
</div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${equipment.length}</div><div style="font-size:9pt;color:#6b7280">Total assets</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#b91c1c">${overdue}</div><div style="font-size:9pt;color:#6b7280">Overdue — action now</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#b45309">${dueSoon}</div><div style="font-size:9pt;color:#6b7280">Due within 30 days</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${equipment.length - overdue - dueSoon}</div><div style="font-size:9pt;color:#6b7280">Current</div></div>
</div>
<div class="legend">
  <div class="leg"><div class="leg-box" style="background:#fee2e2"></div>Overdue / expired</div>
  <div class="leg"><div class="leg-box" style="background:#fef3c7"></div>Due within 30 days</div>
  <div class="leg"><div class="leg-box" style="background:#f0fdf4"></div>Current</div>
  <div class="leg"><div class="leg-box" style="background:#f9fafb;border:1px solid #e5e7eb"></div>Not applicable</div>
</div>
<table>
  <thead><tr>
    <th>Asset #</th><th>Description</th><th>Status</th>
    <th>Rego expiry</th><th>CTP expiry</th><th>Insurance</th><th>Next service</th><th>Calibration</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Confidential — for MCB Civil Electrical internal use · Fleet Compliance Report · Generated ${today}</div>
</body></html>`
  const win = window.open('', '_blank', 'width=1100,height=750')
  if (!win) { alert('Pop-up blocked — allow pop-ups to generate report'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 3 — WORK ORDER PDF GENERATOR
// Formatted work order document sent to the service provider.
// Clearly defines scope, asset, required-by date, estimated cost,
// and authorisation for the mechanic / tyre fitter / fabricator.
// ─────────────────────────────────────────────────────────────────
function generateWorkOrderPDF(wo: WorkOrder, equipment: any[]) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = (n: number | undefined) => n != null ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }) : '—'
  const fmtD   = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
  const equip  = equipment.find(e => e.id === wo.equipmentId)
  const ptMeta = WO_TYPE_META[wo.workType]
  const prMeta = PRIORITY_META[wo.priority]

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Work Order — ${wo.workOrderNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:16mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:14px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;vertical-align:top;border-bottom:1px solid #f3f4f6}
.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.scope-box{border:1px solid #ccc;border-radius:4px;padding:12px 16px;background:#fafafa;margin-bottom:12px;white-space:pre-line;min-height:60px;font-size:10.5pt;line-height:1.7}
.deadline-box{border:2px solid #1B3A6B;border-radius:6px;padding:10px 14px;margin:10px 0;text-align:center}
.priority-CRITICAL{background:#fee2e2;border-color:#fca5a5}
.priority-URGENT{background:#fef3c7;border-color:#fde68a}
.priority-ROUTINE{background:#f0fdf4;border-color:#86efac}
.auth-box{border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;margin:14px 0}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:6px}
.accept-box{border:2px solid #1B3A6B;border-radius:6px;padding:14px 18px;margin-top:14px}
@media print{body{margin:12mm}}
</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500 · fleet@mcbce.com.au</div></div>
  <div style="text-align:right">
    <div style="font-size:18pt;font-weight:900;color:#1B3A6B">WORK ORDER</div>
    <div style="font-family:monospace;font-size:13pt;font-weight:bold;color:#1B3A6B">${wo.workOrderNumber}</div>
    <div style="font-size:9pt;color:#888">Issued: ${now}</div>
  </div>
</div>

<div class="deadline-box priority-${wo.priority}">
  <div style="font-size:9pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">${prMeta?.label ?? wo.priority} PRIORITY — REQUIRED BY</div>
  <div style="font-size:15pt;font-weight:900;color:#1B3A6B">${fmtD(wo.requiredByDate)}</div>
</div>

<h2>Service provider</h2>
<div class="info"><table>
  <tr><td>Company</td><td><strong>${wo.serviceProviderName}</strong></td></tr>
  <tr><td>Work order type</td><td>${ptMeta?.icon ?? ''} ${ptMeta?.label ?? wo.workType.replace(/_/g,' ')}</td></tr>
</table></div>

<h2>Asset details</h2>
<div class="info"><table>
  <tr><td>Asset / vehicle</td><td><strong>${wo.equipmentName}</strong></td></tr>
  <tr><td>Asset number</td><td style="font-family:monospace">${wo.assetNumber}</td></tr>
  ${equip?.registration ? `<tr><td>Registration</td><td>${equip.registration}</td></tr>` : ''}
  ${equip?.year || equip?.make ? `<tr><td>Year / make / model</td><td>${[equip?.year, equip?.make, equip?.model].filter(Boolean).join(' ')}</td></tr>` : ''}
  ${equip?.currentOdometer ? `<tr><td>Current odometer</td><td>${equip.currentOdometer.toLocaleString()} km</td></tr>` : ''}
  ${wo.chargeToJobId ? `<tr><td>Charge to job</td><td>${wo.chargeToJobId}</td></tr>` : ''}
</table></div>

<h2>Scope of work</h2>
<div class="scope-box">${wo.scope}</div>

<h2>Authorisation &amp; costs</h2>
<div class="auth-box">
  <div class="info"><table>
    <tr><td>Estimated cost (ex GST)</td><td><strong>${fmtAUD(wo.estimatedCost)}</strong></td></tr>
    <tr><td>Cost authorisation limit</td><td>Work may proceed up to the estimated cost above without further approval. Any variation exceeding 10% or $200 (whichever is less) must be approved by MCB Civil Electrical before proceeding.</td></tr>
    <tr><td>Invoice to</td><td>MCB Civil Electrical Pty Ltd · ABN 12 345 678 901 · accounts@mcbce.com.au</td></tr>
    <tr><td>Work order reference</td><td style="font-family:monospace;font-weight:bold">Quote ${wo.workOrderNumber} on all invoices</td></tr>
    <tr><td>Payment terms</td><td>30 days from invoice date, subject to work completion sign-off</td></tr>
  </table></div>
</div>

<h2>Conditions</h2>
<p style="font-size:9.5pt;color:#555;line-height:1.7">
1. This work order is issued subject to MCB Civil Electrical standard terms and conditions for fleet services. A copy is available on request.<br>
2. The service provider must notify MCB Civil Electrical immediately if any additional work is identified beyond this scope before proceeding with additional work.<br>
3. The asset must be available to MCB Civil Electrical by the required-by date stated above unless an extension is agreed in writing.<br>
4. All parts used must be genuine OEM or equivalent quality. No reconditioned parts without prior approval.<br>
5. A detailed tax invoice (inc. parts and labour breakdown) must be provided on completion.
</p>

<div class="accept-box">
  <p style="font-size:10pt;font-weight:bold;color:#1B3A6B;margin-bottom:8px">Service provider acceptance</p>
  <p style="font-size:9.5pt;color:#555">By signing below, the service provider confirms acceptance of this work order, the scope of work, and the cost authorisation limit.</p>
  <div class="sig-row">
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Service provider — name, signature &amp; date</div></div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Estimated completion date</div></div>
  </div>
</div>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Authorised by MCB Civil Electrical — name, title &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Work completion — MCB sign-off &amp; date</div></div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · Work Order ${wo.workOrderNumber} · ${wo.equipmentName} (${wo.assetNumber}) · Issued ${now}<br>Enquiries: fleet@mcbce.com.au · 07 3344 5500 · Authorised fleet manager: Sarah Kim</div>
</body></html>`

  const win = window.open('', '_blank', 'width=980,height=800')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print work order'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}


function downloadAssetTemplate() {
  const rows = [
    ['Asset Number','Name','Category','Make','Model','Year','Registration Number','Purchase Cost','Rego Expiry','CTP Expiry','Insurance Expiry','Next Service Date'],
    ['VEH-010','Toyota Hilux SR5 2024','VEHICLE','Toyota','Hilux SR5','2024','2DEF 456','52000','2027-03-15','2027-03-15','2027-06-01','2026-10-01'],
    ['PLT-022','Kubota Mini Excavator U17','PLANT','Kubota','U17','2022','','38000','','','2027-01-10','2026-09-20'],
  ]
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = 'MCB_Asset_Register_Import_Template.csv'
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}


function parseAssetCSVLine(line: string): string[] {
  const out: string[] = []
  let cur = '', inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { cur += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out.map(s => s.trim())
}


function parseAssetCSV(text: string): { assets: any[]; errors: string[] } {
  const rows = text.split(/\r?\n/).filter(r => r.trim().length > 0)
  if (rows.length < 2) return { assets: [], errors: ['File is empty or has no data rows'] }
  const header = parseAssetCSVLine(rows[0]).map(h => h.toLowerCase())
  const idx = {
    num: header.indexOf('asset number'), name: header.indexOf('name'), cat: header.indexOf('category'),
    make: header.indexOf('make'), model: header.indexOf('model'), year: header.indexOf('year'),
    rego: header.indexOf('registration number'), cost: header.indexOf('purchase cost'),
    regoExp: header.indexOf('rego expiry'), ctpExp: header.indexOf('ctp expiry'),
    insExp: header.indexOf('insurance expiry'), svcDate: header.indexOf('next service date'),
  }
  const errors: string[] = []
  const assets: any[] = []
  for (let i = 1; i < rows.length; i++) {
    const cells = parseAssetCSVLine(rows[i])
    const assetNumber = cells[idx.num]?.trim()
    const name = cells[idx.name]?.trim()
    if (!assetNumber || !name) { errors.push(`Row ${i+1}: missing asset number or name — skipped`); continue }
    const purchaseCost = parseFloat(cells[idx.cost]) || 0
    assets.push({
      id: `eq_imported_${Date.now()}_${i}`,
      assetNumber, category: cells[idx.cat]?.trim().toUpperCase() || 'PLANT',
      name, make: cells[idx.make]?.trim() || '—', model: cells[idx.model]?.trim() || '—',
      year: parseInt(cells[idx.year]) || new Date().getFullYear(),
      registrationNumber: cells[idx.rego]?.trim() || null, serialNumber: null,
      status: 'AVAILABLE', currentJobId: null, currentJobNumber: null,
      ownedOrLeased: 'OWNED', purchaseCost, currentValue: purchaseCost,
      regoExpiry: cells[idx.regoExp]?.trim() || null, ctpExpiry: cells[idx.ctpExp]?.trim() || null,
      insuranceExpiry: cells[idx.insExp]?.trim() || null,
      nextServiceDate: cells[idx.svcDate]?.trim() || null, nextServiceKm: null, currentOdometer: 0,
      calibrationDue: null, defects: [],
    })
  }
  return { assets, errors }
}


function AssetImportButton({ onImportMany }: { onImportMany: (rows: any[]) => void }) {
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ count: number; errors: string[] } | null>(null)

  const handleFile = (file: File) => {
    setImporting(true)
    const reader = new FileReader()
    reader.onload = e => {
      const text = String(e.target?.result ?? '')
      const { assets, errors } = parseAssetCSV(text)
      if (assets.length > 0) onImportMany(assets)
      setResult({ count: assets.length, errors })
      setImporting(false)
    }
    reader.onerror = () => { setResult({ count: 0, errors: ['Failed to read file'] }); setImporting(false) }
    reader.readAsText(file)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={downloadAssetTemplate} className="btn-secondary flex items-center gap-1.5 text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Download template
      </button>
      <label className="btn-secondary flex items-center gap-1.5 text-sm cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
        {importing ? 'Importing…' : 'Bulk import CSV'}
        <input type="file" accept=".csv" className="hidden" disabled={importing}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      </label>
      {result && (
        <span className={`text-xs font-medium ${result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {result.count > 0 ? `✓ ${result.count} asset${result.count!==1?'s':''} imported` : ''}
          {result.errors.length > 0 ? ` · ${result.errors.length} skipped` : ''}
        </span>
      )}
    </div>
  )
}

// ── Vehicle / Plant → Employee assignment model ───────────────────
// Vehicles, trucks, and plant are assigned to EMPLOYEES, not to jobs.
// When an employee is assigned to a job, their assigned vehicle/plant follows.
// PM/PC can change the assignment at any time; every change (assign,
// reassign, unassign) is written to a persisted history log — see
// utils/vehicleAssignments.ts and utils/vehicleAssignmentReports.ts.
// An assignment stays in effect until it is explicitly unassigned (behind a
// confirmation safeguard) or the employee is assigned a different asset.

// ── VehicleAssignModal ────────────────────────────────────────────
function VehicleAssignModal({
  assetId, assetName, assetNumber, category, currentAssignment, history,
  onAssign, onUnassign, onClose,
}: {
  assetId: string; assetName: string; assetNumber: string; category: AssignableCategory
  currentAssignment: CurrentAssignment | null
  history: AssignmentHistoryEntry[]
  onAssign: (employeeId: string, employeeName: string, reason: string) => void
  onUnassign: (reason: string) => void
  onClose: () => void
}) {
  const [selectedEmpId, setSelectedEmpId] = useState(currentAssignment?.employeeId ?? '')
  const [reason, setReason] = useState('')
  const [confirmingUnassign, setConfirmingUnassign] = useState(false)
  const [unassignReason, setUnassignReason] = useState('')
  const thisHistory = history.filter(h => h.assetId === assetId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">
              {category === 'PLANT' ? 'Assign plant to employee' : 'Assign vehicle to employee'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{assetNumber} — {assetName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {confirmingUnassign ? (
          // ── Confirmation safeguard for unassign ──────────────────
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <UserX className="w-4 h-4" /> Confirm unassignment
              </div>
              Are you sure you want to unassign <strong>{assetNumber} — {assetName}</strong> from{' '}
              <strong>{currentAssignment?.employeeName}</strong>? This asset will return to the pool and the change will be permanently logged.
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reason for unassignment <span className="text-red-500">*</span></label>
              <input className="input w-full text-sm" value={unassignReason} onChange={e => setUnassignReason(e.target.value)}
                placeholder="e.g. End of job, returning to yard, employee on leave…" autoFocus />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setConfirmingUnassign(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                Back
              </button>
              <button
                onClick={() => {
                  if (!unassignReason.trim()) { alert('Please enter a reason for the unassignment.'); return }
                  onUnassign(unassignReason.trim())
                }}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold">
                Yes, unassign
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                {category === 'PLANT' ? 'Plant' : 'Vehicles'} are assigned to <strong>employees</strong>, not jobs. When the employee is rostered to a job, their assigned {category === 'PLANT' ? 'plant' : 'vehicle'} is implicitly on that site. Assigning a different asset to this employee will automatically end their current assignment. All changes are recorded in the asset's assignment history.
              </div>

              {currentAssignment && (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs">
                  <span className="text-blue-800">
                    Currently assigned to <strong>{currentAssignment.employeeName}</strong> since {formatDate(currentAssignment.effectiveDate)}
                  </span>
                  <button
                    onClick={() => setConfirmingUnassign(true)}
                    className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium flex-shrink-0 ml-2">
                    <UserX className="w-3.5 h-3.5" /> Unassign
                  </button>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign to employee</label>
                <select className="input w-full text-sm" value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
                  <option value="">— Select employee —</option>
                  {MOCK_EMPLOYEES.filter((e: any) => e.employmentType !== 'RESIGNED').map((e: any) => (
                    <option key={e.id} value={e.id}>{e.fullName} ({e.employeeNumber}) — {e.trade}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason for assignment <span className="text-red-500">*</span></label>
                <input className="input w-full text-sm" value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. New job assignment, vehicle swap, replacing prior asset…" />
              </div>
              {thisHistory.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Assignment history</h3>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {thisHistory.map((h) => (
                      <div key={h.id} className="px-3 py-2 text-xs">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-900">
                            {h.action === 'UNASSIGN' ? `Unassigned from ${h.previousEmployeeName ?? 'pool'}` : (h.employeeName ?? 'Unassigned')}
                          </span>
                          <span className="text-gray-400">{h.effectiveDate}</span>
                        </div>
                        <div className="text-gray-500 mt-0.5">{h.reason} · by {h.recordedByName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => {
                if (!selectedEmpId) { alert('Please select an employee to assign.'); return }
                if (!reason.trim()) { alert('Please enter a reason for the assignment change.'); return }
                const emp = MOCK_EMPLOYEES.find((e: any) => e.id === selectedEmpId)
                onAssign(selectedEmpId, emp?.fullName ?? 'Unknown', reason.trim())
              }}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold">
                Confirm assignment
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// UPDATE COMPLIANCE DATES MODAL
// PM/PC uses this when rego, CTP, or insurance is renewed.
// Calls updateEquip() to immediately refresh the dashboard alerts.
// ─────────────────────────────────────────────────────────────────
function UpdateComplianceDatesModal({ equip, currentUserName, onSave, onClose }: {
  equip: Equip
  currentUserName: string
  onSave: (id: string, changes: Partial<Equip>) => void
  onClose: () => void
}) {
  const [regoExpiry,      setRegoExpiry]      = useState(equip.regoExpiry      ?? '')
  const [ctpExpiry,       setCtpExpiry]       = useState(equip.ctpExpiry       ?? '')
  const [insuranceExpiry, setInsuranceExpiry] = useState(equip.insuranceExpiry ?? '')
  const [nextService,     setNextService]     = useState(equip.nextServiceDate  ?? '')
  const [invoiceRef,      setInvoiceRef]      = useState('')
  const [providerName,    setProviderName]    = useState('')
  const [paidDate,        setPaidDate]        = useState('')
  const [docFile,         setDocFile]         = useState<string | null>(null)
  const [docSize,         setDocSize]         = useState('')
  const [notes,           setNotes]           = useState('')
  const [saved,           setSaved]           = useState(false)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setDocFile(f.name)
    setDocSize(f.size > 1024*1024 ? `${(f.size/1024/1024).toFixed(1)} MB` : `${Math.round(f.size/1024)} KB`)
  }

  const handleSave = () => {
    const changes: Partial<Equip> = {}
    if (regoExpiry)      changes.regoExpiry      = regoExpiry
    if (ctpExpiry)       changes.ctpExpiry       = ctpExpiry
    if (insuranceExpiry) changes.insuranceExpiry = insuranceExpiry
    if (nextService)     changes.nextServiceDate = nextService
    onSave(equip.id, changes)
    if (docFile) {
      addComplianceDoc({
        id:              `cd-${Date.now()}`,
        assetId:         equip.id,
        assetNumber:     equip.assetNumber,
        docType:         'INVOICE',
        docTypeLabel:    invoiceRef ? `Invoice ${invoiceRef}` : 'Renewal document',
        fileName:        docFile,
        fileSize:        docSize,
        referenceNumber: invoiceRef,
        policyNumber:    equip.registrationNumber ?? '',
        providerName,
        expiryDate:      regoExpiry || insuranceExpiry || null,
        paidDate:        paidDate || null,
        uploadedAt:      new Date().toISOString(),
        uploadedByName:  currentUserName,
        notes,
      })
    }
    setSaved(true)
  }

  if (saved) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Compliance dates updated</h3>
        <p className="text-sm text-gray-600 mb-1">
          <strong>{equip.assetNumber}</strong> compliance dates have been saved.
        </p>
        <p className="text-xs text-gray-400 mb-4">
          The dashboard compliance alerts will refresh automatically. Any alert for this asset that is now resolved will be cleared.
        </p>
        {docFile && <p className="text-xs text-teal-600 mb-4">Document <strong>{docFile}</strong> recorded in compliance history.</p>}
        <button onClick={onClose} className="btn-primary w-full">Done</button>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-blue-50 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              <h2 className="font-bold text-gray-900">Update compliance dates</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{equip.assetNumber} — {equip.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Current vs new dates */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Compliance dates — enter new expiry dates after renewal</p>
            <div className="space-y-3">
              {[
                { label: 'Registration expiry',  current: equip.regoExpiry,      val: regoExpiry,      set: setRegoExpiry,      status: complianceStatus(equip.regoExpiry) },
                { label: 'CTP expiry',            current: equip.ctpExpiry,       val: ctpExpiry,       set: setCtpExpiry,       status: complianceStatus(equip.ctpExpiry)  },
                { label: 'Insurance expiry',      current: equip.insuranceExpiry, val: insuranceExpiry, set: setInsuranceExpiry, status: complianceStatus(equip.insuranceExpiry) },
                { label: 'Next service date',     current: equip.nextServiceDate, val: nextService,     set: setNextService,     status: complianceStatus(equip.nextServiceDate) },
              ].map(f => {
                const sm = COMPLIANCE_META[f.status]
                return (
                  <div key={f.label} className={`border rounded-xl p-3.5 ${f.status !== 'NONE' && f.status !== 'CURRENT' ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-700">{f.label}</label>
                      <div className="flex items-center gap-2">
                        {f.current && (
                          <span className="text-xs text-gray-400">Current: {formatDate(f.current)}</span>
                        )}
                        {f.status !== 'NONE' && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                        )}
                      </div>
                    </div>
                    <input type="date" value={f.val}
                      onChange={e => f.set(e.target.value)}
                      className="input text-sm w-full" />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Invoice / document details */}
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Paid invoice / renewal document <span className="text-gray-400 font-normal">(optional)</span></p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Invoice / certificate #</label>
                <input className="input text-sm w-full" value={invoiceRef} onChange={e => setInvoiceRef(e.target.value)}
                  placeholder="e.g. INV-2026-4421" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Provider / insurer</label>
                <input className="input text-sm w-full" value={providerName} onChange={e => setProviderName(e.target.value)}
                  placeholder="e.g. RACQ Insurance" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date paid</label>
                <input type="date" className="input text-sm w-full" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Attach document</label>
                <label className={`flex items-center gap-2 text-xs border-2 border-dashed rounded-lg px-3 py-2 cursor-pointer transition-colors ${docFile ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'}`}>
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{docFile ? `${docFile} (${docSize})` : 'Click to attach PDF / image'}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="sr-only" onChange={handleFile} />
                </label>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea className="input w-full resize-none text-sm h-16" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Annual fleet policy renewed — all VEH assets covered. Certificate sent to accounts." />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex gap-3 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary flex-shrink-0">Cancel</button>
          <button onClick={handleSave}
            disabled={!regoExpiry && !ctpExpiry && !insuranceExpiry && !nextService}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-40">
            <CheckCircle className="w-4 h-4" />
            Save compliance dates
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// COMPLIANCE DOCUMENT HISTORY MODAL
// Shows all documents attached to an asset's compliance record.
// ─────────────────────────────────────────────────────────────────
function ComplianceDocsModal({ equip, currentUserName, onClose }: {
  equip: Equip
  currentUserName: string
  onClose: () => void
}) {
  const [docs, setDocs]         = useState<ComplianceDocument[]>(() => getComplianceDocs(equip.id))
  const [docType, setDocType]   = useState<ComplianceDocType>('INVOICE')
  const [fileName, setFileName] = useState('')
  const [fileSize, setFileSize] = useState('')
  const [refNum, setRefNum]     = useState('')
  const [policyNum, setPolicyNum] = useState(equip.registrationNumber ?? '')
  const [provider, setProvider] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [paidDate, setPaidDate] = useState('')
  const [notes, setNotes]       = useState('')
  const [adding, setAdding]     = useState(false)

  const DOC_TYPE_LABELS: Record<ComplianceDocType, string> = {
    REGO_CERT:       'Registration certificate',
    CTP_RECEIPT:     'CTP receipt',
    INSURANCE_CERT:  'Insurance certificate',
    INVOICE:         'Paid invoice',
    OTHER:           'Other document',
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFileName(f.name)
    setFileSize(f.size > 1024*1024 ? `${(f.size/1024/1024).toFixed(1)} MB` : `${Math.round(f.size/1024)} KB`)
  }

  const handleAdd = () => {
    if (!fileName) return
    const doc: ComplianceDocument = {
      id: `cd-${Date.now()}`, assetId: equip.id, assetNumber: equip.assetNumber,
      docType, docTypeLabel: DOC_TYPE_LABELS[docType],
      fileName, fileSize, referenceNumber: refNum, policyNumber: policyNum,
      providerName: provider, expiryDate: expiryDate || null, paidDate: paidDate || null,
      uploadedAt: new Date().toISOString(), uploadedByName: currentUserName, notes,
    }
    addComplianceDoc(doc)
    setDocs(getComplianceDocs(equip.id))
    setAdding(false)
    setFileName(''); setFileSize(''); setRefNum(''); setProvider(''); setExpiryDate(''); setPaidDate(''); setNotes('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600" />
              <h2 className="font-bold text-gray-900">Compliance documents</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{equip.assetNumber} — {equip.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {docs.length === 0 && !adding && (
            <div className="text-center py-8 text-gray-400">
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No documents uploaded yet</p>
              <p className="text-xs mt-1">Attach paid invoices, certificates, and renewal receipts to maintain a complete compliance record.</p>
            </div>
          )}

          {docs.map(d => (
            <div key={d.id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-sm text-gray-900 truncate">{d.fileName}</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{d.docTypeLabel}</span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5">
                    {d.referenceNumber && <div>Ref: <span className="font-medium">{d.referenceNumber}</span></div>}
                    {d.providerName    && <div>Provider: {d.providerName}</div>}
                    {d.paidDate        && <div>Paid: {formatDate(d.paidDate)}</div>}
                    {d.expiryDate      && <div>Covers to: <span className="font-medium">{formatDate(d.expiryDate)}</span></div>}
                    {d.notes           && <div className="text-gray-400 italic">{d.notes}</div>}
                    <div className="text-gray-400">Uploaded {new Date(d.uploadedAt).toLocaleDateString('en-AU')} by {d.uploadedByName} · {d.fileSize}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {adding ? (
            <div className="border-2 border-dashed border-teal-300 rounded-xl p-4 bg-teal-50/30 space-y-3">
              <p className="text-xs font-semibold text-teal-700">Add compliance document</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Document type</label>
                  <select className="input text-sm" value={docType} onChange={e => setDocType(e.target.value as ComplianceDocType)}>
                    {(Object.entries(DOC_TYPE_LABELS) as [ComplianceDocType, string][]).map(([k,v]) =>
                      <option key={k} value={k}>{v}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice / certificate #</label>
                  <input className="input text-sm w-full" value={refNum} onChange={e => setRefNum(e.target.value)}
                    placeholder="e.g. INV-2026-4421" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Provider / insurer</label>
                  <input className="input text-sm w-full" value={provider} onChange={e => setProvider(e.target.value)}
                    placeholder="e.g. RACQ, Allianz" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date paid</label>
                  <input type="date" className="input text-sm w-full" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Covers to (expiry)</label>
                  <input type="date" className="input text-sm w-full" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Attach file</label>
                  <label className={`flex items-center gap-2 text-xs border-2 border-dashed rounded-lg px-3 py-2 cursor-pointer transition-colors ${fileName ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-400'}`}>
                    <Paperclip className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{fileName || 'Click to attach'}</span>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="sr-only" onChange={handleFile} />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <input className="input text-sm w-full" value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Optional notes about this document" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setAdding(false)} className="btn-secondary text-sm flex-shrink-0">Cancel</button>
                <button onClick={handleAdd} disabled={!fileName}
                  className="btn-primary text-sm flex-1 disabled:opacity-40">Save document</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)}
              className="w-full flex items-center justify-center gap-2 text-sm border-2 border-dashed border-gray-300 hover:border-teal-400 text-gray-500 hover:text-teal-600 rounded-xl py-3 transition-colors">
              <Paperclip className="w-4 h-4" />
              Attach invoice or certificate
            </button>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary w-full">Close</button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// FLEET COMPLIANCE TAB — main page tab
// Shows all fleet assets with rego / CTP / insurance status.
// PM/PC can update dates and attach renewal documents here.
// Dashboard alerts auto-refresh because getComplianceAlerts()
// is called with the updated equipment state on each render.
// ─────────────────────────────────────────────────────────────────
function FleetComplianceTab({ equipment, canManage, currentUserName, onUpdateEquip }: {
  equipment:       Equip[]
  canManage:       boolean
  currentUserName: string
  onUpdateEquip:   (id: string, changes: Partial<Equip>) => void
}) {
  const [updateTarget, setUpdateTarget]   = useState<Equip | null>(null)
  const [docsTarget,   setDocsTarget]     = useState<Equip | null>(null)
  const [catFilter,    setCatFilter]      = useState('ALL')
  const [statusFilter, setStatusFilter]   = useState('ALL')
  const [docCounts,    setDocCounts]      = useState<Record<string, number>>({})

  // Refresh doc counts when modals close
  const refreshDocCounts = () => {
    const counts: Record<string, number> = {}
    for (const eq of equipment) counts[eq.id] = getComplianceDocs(eq.id).length
    setDocCounts(counts)
  }

  // Assets that have at least one compliance date (rego/CTP/insurance relevant)
  const complianceAssets = equipment.filter(eq =>
    ['VEHICLE', 'PLANT', 'TRAILER'].includes(eq.category) ||
    eq.regoExpiry || eq.ctpExpiry || eq.insuranceExpiry
  )

  const filtered = complianceAssets
    .filter(eq => catFilter === 'ALL' || eq.category === catFilter)
    .filter(eq => {
      if (statusFilter === 'ALL') return true
      return worstComplianceStatus(eq) === statusFilter
    })

  // Summary counts
  const overdue  = complianceAssets.filter(e => worstComplianceStatus(e) === 'OVERDUE').length
  const critical = complianceAssets.filter(e => worstComplianceStatus(e) === 'CRITICAL').length
  const warning  = complianceAssets.filter(e => worstComplianceStatus(e) === 'WARNING').length
  const upcoming = complianceAssets.filter(e => worstComplianceStatus(e) === 'UPCOMING').length
  const current  = complianceAssets.filter(e => worstComplianceStatus(e) === 'CURRENT').length

  const cats = [...new Set(complianceAssets.map(e => e.category))]

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Overdue',     val: overdue,  bg: 'bg-red-100',    text: 'text-red-700',    filter: 'OVERDUE'  },
          { label: '≤14 days',   val: critical, bg: 'bg-red-50',     text: 'text-red-600',    filter: 'CRITICAL' },
          { label: '≤30 days',   val: warning,  bg: 'bg-amber-100',  text: 'text-amber-700',  filter: 'WARNING'  },
          { label: '≤60 days',   val: upcoming, bg: 'bg-yellow-100', text: 'text-yellow-700', filter: 'UPCOMING' },
          { label: 'All current', val: current,  bg: 'bg-green-100',  text: 'text-green-700',  filter: 'CURRENT'  },
        ].map(c => (
          <button key={c.filter} onClick={() => setStatusFilter(statusFilter === c.filter ? 'ALL' : c.filter)}
            className={`${c.bg} border ${statusFilter === c.filter ? 'border-current ring-2 ring-current ring-offset-1' : 'border-transparent'} rounded-xl p-4 text-left transition-all`}>
            <div className={`text-2xl font-bold ${c.text}`}>{c.val}</div>
            <div className={`text-xs mt-0.5 ${c.text}`}>{c.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select className="input text-sm w-auto" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="ALL">All categories</option>
          {cats.map(c => <option key={c} value={c}>{CAT_LABELS[c]?.label ?? c}</option>)}
        </select>
        {statusFilter !== 'ALL' && (
          <button onClick={() => setStatusFilter('ALL')} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X className="w-3 h-3" /> Clear filter
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} of {complianceAssets.length} assets</span>
      </div>

      {/* Fleet table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-14 text-center text-gray-400">
            <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No assets match the current filter</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 font-medium">Asset</th>
                  <th className="text-left px-3 py-3 font-medium">Rego #</th>
                  <th className="text-center px-3 py-3 font-medium">Registration</th>
                  <th className="text-center px-3 py-3 font-medium">CTP</th>
                  <th className="text-center px-3 py-3 font-medium">Insurance</th>
                  <th className="text-center px-3 py-3 font-medium">Overall</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(eq => {
                  const worst  = worstComplianceStatus(eq)
                  const wsm    = COMPLIANCE_META[worst]
                  const rSm    = COMPLIANCE_META[complianceStatus(eq.regoExpiry)]
                  const cSm    = COMPLIANCE_META[complianceStatus(eq.ctpExpiry)]
                  const iSm    = COMPLIANCE_META[complianceStatus(eq.insuranceExpiry)]
                  const cat    = CAT_LABELS[eq.category]
                  const numDoc = docCounts[eq.id] ?? getComplianceDocs(eq.id).length
                  const needsAttention = ['OVERDUE','CRITICAL','WARNING'].includes(worst)

                  return (
                    <tr key={eq.id} className={`${needsAttention ? 'bg-amber-50/20' : 'bg-white'} hover:bg-gray-50`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1.5">
                              <span className="font-mono text-xs text-gray-400">{eq.assetNumber}</span>
                              <span className="text-sm">{eq.name}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{cat?.icon} {cat?.label}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs font-mono text-gray-600">{eq.registrationNumber ?? '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {eq.regoExpiry ? (
                          <div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rSm.bg} ${rSm.text}`}>
                              {rSm.label}
                            </span>
                            <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(eq.regoExpiry)}</div>
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {eq.ctpExpiry ? (
                          <div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cSm.bg} ${cSm.text}`}>
                              {cSm.label}
                            </span>
                            <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(eq.ctpExpiry)}</div>
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {eq.insuranceExpiry ? (
                          <div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${iSm.bg} ${iSm.text}`}>
                              {iSm.label}
                            </span>
                            <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(eq.insuranceExpiry)}</div>
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${wsm.bg} ${wsm.text}`}>
                          {worst === 'NONE' ? '—' : wsm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canManage && (
                            <button onClick={() => setUpdateTarget(eq)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1 transition-colors">
                              <Shield className="w-3 h-3" />
                              Update dates
                            </button>
                          )}
                          <button onClick={() => { setDocsTarget(eq); refreshDocCounts() }}
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1 transition-colors">
                            <Paperclip className="w-3 h-3" />
                            {numDoc > 0 ? `Docs (${numDoc})` : 'Docs'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Note for non-editable tiers */}
      {!canManage && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
          <strong>View only.</strong> Compliance date updates require PM or PC access. Contact your project manager to update rego or insurance dates after renewal.
        </div>
      )}

      {/* Modals */}
      {updateTarget && (
        <UpdateComplianceDatesModal
          equip={updateTarget}
          currentUserName={currentUserName}
          onSave={(id, changes) => { onUpdateEquip(id, changes); refreshDocCounts() }}
          onClose={() => { setUpdateTarget(null); refreshDocCounts() }}
        />
      )}
      {docsTarget && (
        <ComplianceDocsModal
          equip={docsTarget}
          currentUserName={currentUserName}
          onClose={() => { setDocsTarget(null); refreshDocCounts() }}
        />
      )}
    </div>
  )
}


export function PlantEquipment() {
  const { isTier, user } = useAuthStore()
  const [equipment,        setEquipment]        = useState<Equip[]>(MOCK_EQUIPMENT as Equip[])

  // Live asset count from Neon
  const { data: liveAssetsRes } = useQuery<any[]>({
    queryKey: ['plant-assets'],
    queryFn:  fetchPlantAssets,
    staleTime: 60_000,
  })
  const liveAssetCount: number = (liveAssetsRes as any[])?.length ?? 0
  const [currentAssignments, setCurrentAssignments] = useState<CurrentAssignment[]>(() => getCurrentAssignments())
  const [assignmentHistory, setAssignmentHistory]   = useState<AssignmentHistoryEntry[]>(() => getAssignmentHistory())
  const [assignModal, setAssignModal]             = useState<{ assetId: string; assetName: string; assetNumber: string; category: AssignableCategory } | null>(null)
  const [workOrders,       setWorkOrders]        = useState<WorkOrder[]>(INITIAL_WORK_ORDERS)
  const [serviceProviders, setServiceProviders]  = useState<ServiceProvider[]>(INITIAL_SERVICE_PROVIDERS)
  const [selected,         setSelected]          = useState<Equip | null>(null)
  const [pageTab,          setPageTab]           = useState<'register'|'workorders'|'providers'|'assignments'|'rego'>('register')
  const [search,           setSearch]            = useState('')
  const [catFilter,        setCatFilter]         = useState('ALL')
  const [statusFilter,     setStatusFilter]      = useState('ALL')
  const [showAddEquip,     setShowAddEquip]      = useState(false)
  const [showAddWO,        setShowAddWO]         = useState(false)
  const [showAddSP,        setShowAddSP]         = useState(false)
  const [spSearch,         setSpSearch]          = useState('')

  const alerts = getComplianceAlerts(equipment as typeof MOCK_EQUIPMENT)
  const canManage = isTier('B_PC')

  const refreshAssignments = () => {
    setCurrentAssignments(getCurrentAssignments())
    setAssignmentHistory(getAssignmentHistory())
  }

  const updateEquip = (id: string, changes: Partial<Equip>) =>
    setEquipment(prev => prev.map(e => e.id === id ? { ...e, ...changes } : e))

  const getAssignmentFor = (assetId: string) => currentAssignments.find(a => a.assetId === assetId) ?? null

  // Show equipment detail
  if (selected) {
    const current = equipment.find(e => e.id === selected.id) ?? selected
    return (
      <>
        <EquipDetail
          equip={current} workOrders={workOrders} serviceProviders={serviceProviders} equipment={equipment}
          currentAssignment={getAssignmentFor(current.id)}
          onOpenAssign={() => setAssignModal({ assetId: current.id, assetName: current.name, assetNumber: current.assetNumber, category: current.category as AssignableCategory })}
          onBack={() => setSelected(null)}
          onAllocate={(jId, jNum) => updateEquip(current.id, { status: 'ALLOCATED', currentJobId: jId, currentJobNumber: jNum })}
          onDeallocate={() => updateEquip(current.id, { status: 'AVAILABLE', currentJobId: null, currentJobNumber: null })}
          onDefect={(desc, sev) => {
            updateEquip(current.id, {
              defects: [...current.defects, { id: `d_new_${Date.now()}`, severity: sev, description: desc, status: 'OPEN', reportedAt: new Date().toISOString().slice(0,10) }],
              ...(sev === 'CRITICAL' ? { status: 'OUT_OF_SERVICE' } : {}),
            })
            // Persist to Neon for real assets (UUID IDs)
            if (isNeonAsset(current.id)) {
              reportDefectToNeon(current.id, { description: desc, severity: sev })
            }
          }}
          onCreateWO={wo => setWorkOrders(prev => [wo, ...prev])}
          onCompleteWO={(woId, equipId, nextServiceDate) => {
            // P3-8: Mark WO completed and update next service date on the asset
            setWorkOrders(prev => prev.map(wo =>
              wo.id === woId ? { ...wo, status: 'COMPLETED' as const, completedAt: new Date().toISOString() } : wo
            ))
            updateEquip(equipId, { nextServiceDate })
          }}
        />
        {assignModal && (
          <VehicleAssignModal
            assetId={assignModal.assetId} assetName={assignModal.assetName} assetNumber={assignModal.assetNumber} category={assignModal.category}
            currentAssignment={getAssignmentFor(assignModal.assetId)}
            history={assignmentHistory}
            onAssign={(empId, empName, reason) => {
              assignAssetToEmployee(
                { id: assignModal.assetId, name: assignModal.assetName, assetNumber: assignModal.assetNumber, category: assignModal.category },
                empId, empName, reason, user?.fullName ?? 'PM/PC',
              )
              refreshAssignments()
              setAssignModal(null)
            }}
            onUnassign={(reason) => {
              unassignAsset(assignModal.assetId, reason, user?.fullName ?? 'PM/PC')
              refreshAssignments()
              setAssignModal(null)
            }}
            onClose={() => setAssignModal(null)}
          />
        )}
      </>
    )
  }

  const filtered = equipment.filter(e => {
    const matchS  = !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.assetNumber.toLowerCase().includes(search.toLowerCase())
    const matchC  = catFilter  === 'ALL' || e.category === catFilter
    const matchSt = statusFilter === 'ALL' || e.status  === statusFilter
    return matchS && matchC && matchSt
  })

  const filteredSPs = serviceProviders.filter(sp =>
    !spSearch || sp.companyName.toLowerCase().includes(spSearch.toLowerCase()) ||
    sp.servicesOffered.toLowerCase().includes(spSearch.toLowerCase())
  )

  const openWOCount = workOrders.filter(w => !['COMPLETED','CANCELLED'].includes(w.status)).length

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plant &amp; Equipment</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {equipment.length} items registered
            {liveAssetCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveAssetCount} live from Neon</span>}
            {alerts.length > 0 && <span className="ml-2 text-amber-600 font-medium">· {alerts.length} compliance alert{alerts.length !== 1 ? 's' : ''}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {canManage && pageTab === 'register' && (
            <>
              <AssetImportButton onImportMany={rows => setEquipment(prev => [...prev, ...rows])} />
              <button onClick={() => generateFleetComplianceHTML(equipment)} className="btn-secondary flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                Fleet compliance
              </button>
              <button onClick={() => setShowAddEquip(true)}
                className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add equipment
              </button>
            </>
          )}
          {canManage && pageTab === 'workorders' && (
            <button onClick={() => setShowAddWO(true)}
              className="btn-primary flex items-center gap-2">
              <ClipboardList className="w-4 h-4" /> New work order
            </button>
          )}
          {canManage && pageTab === 'providers' && (
            <button onClick={() => setShowAddSP(true)}
              className="btn-primary flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Register provider
            </button>
          )}
          {canManage && pageTab === 'assignments' && (
            <>
              <button onClick={() => downloadWeeklyAssignmentCsv(getHistoryForWeek())} className="btn-secondary flex items-center gap-2">
                <FileDown className="w-4 h-4" /> Export this week (CSV)
              </button>
              <button onClick={() => downloadWeeklyAssignmentJson(getHistoryForWeek())} className="btn-secondary flex items-center gap-2">
                <FileDown className="w-4 h-4" /> Export this week (JSON)
              </button>
            </>
          )}
        </div>
      </div>

      {/* Compliance alerts banner */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Compliance alerts — {alerts.length} item{alerts.length !== 1 ? 's' : ''} require attention</span>
          </div>
          <div className="space-y-1.5">
            {alerts.map(a => (
              <div key={a.id} className={`flex items-center justify-between text-xs rounded-lg px-3 py-2 ${a.severity === 'HIGH' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                <span><strong>{a.assetNumber}</strong> · {a.type}</span>
                <span className="font-bold">{a.daysLeft <= 0 ? 'OVERDUE' : `${a.daysLeft}d`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { id: 'register',   label: `Equipment (${equipment.length})` },
          { id: 'rego',       label: 'Rego & Insurance', alert: alerts.some(a => ['Registration','CTP Insurance','Insurance'].includes(a.type)) },
          { id: 'workorders', label: `Work orders (${openWOCount})`, alert: openWOCount > 0 },
          { id: 'providers',  label: `Service providers (${serviceProviders.length})` },
          { id: 'assignments', label: `Assignments (${currentAssignments.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setPageTab(t.id as any)}
            className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors relative ${pageTab===t.id?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {(t as any).alert && <span className="absolute top-1 right-2 w-1.5 h-1.5 bg-amber-500 rounded-full" />}
          </button>
        ))}
      </div>

      {/* ── TAB: REGISTER ──────────────────────────────────────── */}
      {pageTab === 'rego' && (
        <FleetComplianceTab equipment={equipment} canManage={canManage} currentUserName={user?.fullName ?? 'PM/PC'} onUpdateEquip={updateEquip} />
      )}

      {pageTab === 'register' && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className="input pl-9" placeholder="Name, asset number…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="input text-sm w-auto" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
              <option value="ALL">All categories</option>
              {Object.entries(CAT_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="input text-sm w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="ALL">All statuses</option>
              {Object.keys(STATUS_STYLE).map(k => <option key={k} value={k}>{STATUS_STYLE[k].label}</option>)}
            </select>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="divide-y divide-gray-100">
              {filtered.length === 0
                ? <div className="py-14 text-center text-gray-400"><Truck className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No equipment matches</p></div>
                : filtered.map(eq => {
                    const ss  = STATUS_STYLE[eq.status] ?? STATUS_STYLE.AVAILABLE
                    const cat = CAT_LABELS[eq.category] ?? { label: eq.category, icon: '📦', prefix: 'OTH' }
                    const hasAlert       = alerts.some(a => a.assetNumber === eq.assetNumber)
                    // PM-12: severity-split defect gate
                    const openDefects    = eq.defects.filter((d:any) => d.status === 'OPEN')
                    const hasCritDefect  = openDefects.some((d:any) => d.severity === 'CRITICAL' || d.severity === 'MAJOR')
                    const hasMinorDefect = openDefects.some((d:any) => d.severity === 'MINOR') && !hasCritDefect
                    const hasWO          = workOrders.some(w => w.equipmentId === eq.id && !['COMPLETED','CANCELLED'].includes(w.status))
                    const isAssignable   = eq.category === 'VEHICLE' || eq.category === 'PLANT'
                    const assignment     = isAssignable ? getAssignmentFor(eq.id) : null
                    return (
                      <div key={eq.id} onClick={() => setSelected(eq)}
                        className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 cursor-pointer group transition-colors">
                        <div className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: ss.hex }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="font-mono text-xs text-gray-500">{eq.assetNumber}</span>
                            <span className="font-semibold text-sm text-gray-900 truncate">{eq.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ss.badge}`}>{ss.label}</span>
                            {hasAlert      && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Compliance</span>}
                            {hasCritDefect  && <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />DEFECT — BLOCKED</span>}
                            {hasMinorDefect && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Minor defect</span>}
                            {hasWO          && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">Work order</span>}
                          </div>
                          <p className="text-xs text-gray-500">
                            {cat.icon} {cat.label}{eq.currentJobNumber ? ` · ${eq.currentJobNumber}` : ''}
                            {isAssignable && (
                              <span className={assignment ? 'text-gray-500' : 'text-gray-400'}>
                                {' '}· {assignment ? <>Assigned to <strong className="text-gray-700">{assignment.employeeName}</strong></> : 'Unassigned (pool)'}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="hidden sm:block text-right text-xs text-gray-400 flex-shrink-0 w-24">
                          {eq.nextServiceDate && <div>Service {formatDate(eq.nextServiceDate)}</div>}
                          {eq.regoExpiry      && <div>Rego {formatDate(eq.regoExpiry)}</div>}
                        </div>
                        {isAssignable && canManage && (
                          hasCritDefect
                            ? <button
                                onClick={(e) => { e.stopPropagation(); alert(`⛔ Cannot allocate ${eq.assetNumber} — ${openDefects.filter((d:any) => d.severity === 'CRITICAL' || d.severity === 'MAJOR').map((d:any) => d.description).join('; ')}. Close defect before allocating.`) }}
                                className="hidden sm:flex items-center gap-1 text-xs text-red-600 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-lg flex-shrink-0 font-medium">
                                <AlertTriangle className="w-3.5 h-3.5" />Blocked
                              </button>
                            : <button
                                onClick={(e) => { e.stopPropagation(); setAssignModal({ assetId: eq.id, assetName: eq.name, assetNumber: eq.assetNumber, category: eq.category as AssignableCategory }) }}
                                className="hidden sm:flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg border border-indigo-200 flex-shrink-0">
                                <UserCog className="w-3.5 h-3.5" />{assignment ? 'Reassign' : 'Assign'}
                              </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                      </div>
                    )
                  })
              }
            </div>
          </div>
        </>
      )}

      {/* ── TAB: WORK ORDERS ──────────────────────────────────── */}
      {pageTab === 'workorders' && (
        <div className="space-y-3">
          {workOrders.length === 0
            ? <div className="text-center py-16 bg-white rounded-xl border border-gray-200 text-gray-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                <p className="font-medium">No work orders yet</p>
                <p className="text-sm mt-1">Create a work order from any equipment record</p>
              </div>
            : workOrders.sort((a,b)=>b.createdAt.localeCompare(a.createdAt)).map(wo => {
              const sm = WO_STATUS_META[wo.status]
              const tm = WO_TYPE_META[wo.workType]
              return (
                <div key={wo.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">{tm.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-mono text-gray-400">{wo.workOrderNumber}</span>
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${sm.bg} ${sm.text}`}>{sm.label}</span>
                        <span className={`text-xs font-semibold ${PRIORITY_META[wo.priority].color}`}>{PRIORITY_META[wo.priority].label}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{wo.assetNumber}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{wo.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{wo.equipmentName}</p>
                      {wo.scope && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{wo.scope}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{wo.serviceProviderName}</span>
                        {wo.estimatedCost && <span>{formatCurrency(wo.estimatedCost)} est.</span>}
                        {wo.requiredByDate && <span>Due {formatDate(wo.requiredByDate)}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          }
        </div>
      )}

      {/* ── TAB: SERVICE PROVIDERS ────────────────────────────── */}
      {pageTab === 'providers' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-9 w-full" placeholder="Search service providers…" value={spSearch} onChange={e => setSpSearch(e.target.value)} />
          </div>

          <div className="space-y-3">
            {filteredSPs.map(sp => {
              const cm = SP_CAT_META[sp.subCategory]
              return (
                <div key={sp.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl ${cm.bg}`}>
                      {cm.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-900 text-sm">{sp.companyName}</span>
                        {sp.isPreferred && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Star className="w-3 h-3" />Preferred</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${cm.bg} ${cm.text}`}>{cm.label}</span>
                      </div>
                      <p className="text-xs text-gray-500">{sp.servicesOffered}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{sp.contactPhone}</span>
                        {sp.contactEmail && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{sp.contactEmail}</span>}
                        <span>Lead {sp.leadTimeDays}d</span>
                        {sp.abn && <span className="font-mono">ABN {sp.abn}</span>}
                      </div>
                      {sp.address && <p className="text-xs text-gray-400 mt-1">{sp.address}</p>}
                      {sp.notes && <p className="text-xs text-indigo-600 bg-indigo-50 rounded px-2 py-1 mt-2">{sp.notes}</p>}
                    </div>
                    <button
                      onClick={() => { setShowAddWO(true) }}
                      className="text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg border border-indigo-200 flex-shrink-0">
                      + Work order
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── TAB: ASSIGNMENTS (vehicle / plant → employee) ──────── */}
      {pageTab === 'assignments' && (() => {
        const { start, end } = getWeekRange()
        const weekHistory = getHistoryForWeek()
        return (
          <div className="space-y-5">
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-indigo-800">
                <span className="font-semibold">This week's log:</span> {formatDate(start.toISOString())} – {formatDate(end.toISOString())} · {weekHistory.length} change{weekHistory.length !== 1 ? 's' : ''} recorded
              </div>
              <div className="flex gap-2">
                <button onClick={() => downloadFullAssignmentHistoryCsv(assignmentHistory)} className="text-xs text-indigo-700 bg-white hover:bg-indigo-100 px-3 py-1.5 rounded-lg border border-indigo-200 flex items-center gap-1">
                  <FileDown className="w-3.5 h-3.5" /> Full history (CSV)
                </button>
                <button onClick={() => downloadCurrentAssignmentsCsv(currentAssignments)} className="text-xs text-indigo-700 bg-white hover:bg-indigo-100 px-3 py-1.5 rounded-lg border border-indigo-200 flex items-center gap-1">
                  <FileDown className="w-3.5 h-3.5" /> Current snapshot (CSV)
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Currently assigned</h3>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {currentAssignments.length === 0
                  ? <div className="text-center py-14 text-gray-400"><UserCog className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No vehicles or plant are currently assigned</p></div>
                  : (
                    <div className="divide-y divide-gray-100">
                      {currentAssignments.slice().sort((a,b) => a.assetNumber.localeCompare(b.assetNumber)).map(a => (
                        <div key={a.assetId} className="flex items-center gap-4 px-5 py-3 text-sm">
                          <span className="font-mono text-xs text-gray-500 w-20 flex-shrink-0">{a.assetNumber}</span>
                          <span className="flex-1 min-w-0 truncate">{a.assetName}</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0">{CAT_LABELS[a.category]?.label ?? a.category}</span>
                          <span className="font-medium text-gray-900 flex-shrink-0">{a.employeeName}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0 w-24 text-right">since {formatDate(a.effectiveDate)}</span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><History className="w-4 h-4" /> Full assignment history log</h3>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {assignmentHistory.length === 0
                  ? <div className="text-center py-14 text-gray-400"><p className="text-sm">No assignment activity recorded yet</p></div>
                  : (
                    <div className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
                      {assignmentHistory.map(h => (
                        <div key={h.id} className="px-5 py-3 text-sm">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              h.action === 'UNASSIGN' ? 'bg-red-100 text-red-700' : h.action === 'REASSIGN' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                            }`}>{h.action}</span>
                            <span className="font-mono text-xs text-gray-500">{h.assetNumber}</span>
                            <span className="font-medium text-gray-900">{h.assetName}</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            {h.action === 'UNASSIGN'
                              ? <>Unassigned from <strong>{h.previousEmployeeName ?? 'pool'}</strong></>
                              : <>Assigned to <strong>{h.employeeName}</strong>{h.previousEmployeeName ? <> (was {h.previousEmployeeName})</> : null}</>
                            } · {h.reason}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(h.recordedAt)} · recorded by {h.recordedByName}</p>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── MODALS ────────────────────────────────────────────── */}
      {showAddEquip && (
        <AddEquipmentModal
          existing={equipment}
          onClose={() => setShowAddEquip(false)}
          onAdd={e => {
            setEquipment(prev => [e, ...prev])
            // Persist to Neon (fire-and-forget)
            createPlantAsset({
              assetNumber:      e.assetNumber,
              name:             e.name,
              category:         e.category,
              make:             e.make            || undefined,
              model:            e.model           || undefined,
              year:             (e as any).year   || undefined,
              registrationNumber: e.registrationNumber || undefined,
              purchaseCost:     e.purchaseCost    || undefined,
              regoExpiry:       e.regoExpiry      || undefined,
              ctpExpiry:        e.ctpExpiry       || undefined,
              insuranceExpiry:  e.insuranceExpiry || undefined,
              nextServiceDate:  e.nextServiceDate || undefined,
            })
          }}
        />
      )}
      {showAddWO && (
        <CreateWorkOrderModal
          equipment={equipment}
          serviceProviders={serviceProviders}
          onClose={() => setShowAddWO(false)}
          onSave={wo => setWorkOrders(prev => [wo, ...prev])}
        />
      )}
      {showAddSP && (
        <AddServiceProviderModal
          onClose={() => setShowAddSP(false)}
          onAdd={sp => setServiceProviders(prev => [sp, ...prev])}
        />
      )}

      {/* Vehicle / plant assignment modal */}
      {assignModal && (
        <VehicleAssignModal
          assetId={assignModal.assetId} assetName={assignModal.assetName} assetNumber={assignModal.assetNumber} category={assignModal.category}
          currentAssignment={getAssignmentFor(assignModal.assetId)}
          history={assignmentHistory}
          onAssign={(empId, empName, reason) => {
            assignAssetToEmployee(
              { id: assignModal.assetId, name: assignModal.assetName, assetNumber: assignModal.assetNumber, category: assignModal.category },
              empId, empName, reason, user?.fullName ?? 'PM/PC',
            )
            refreshAssignments()
            setAssignModal(null)
          }}
          onUnassign={(reason) => {
            unassignAsset(assignModal.assetId, reason, user?.fullName ?? 'PM/PC')
            refreshAssignments()
            setAssignModal(null)
          }}
          onClose={() => setAssignModal(null)}
        />
      )}
    </div>
  )
}
